import { useState, useEffect, useRef, useCallback, useMemo } from "react"; // ✅ added useMemo
import { useNavigate } from "react-router-dom";
import { Modal, Form } from "react-bootstrap";
import { Page, Grid, Cell } from "./ui/Layout";
import QuoteTicker from "./ui/QuoteTicker";
import RowSheet, { useIsMobile } from "./ui/RowSheet";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  List,
  ListItem,
} from "./ui/Primitives";
import {
  FaUser,
  FaGraduationCap,
  FaUsers,
  FaClock,
  FaEdit,
  FaEye,
  FaVideo,
  FaChartLine,
  FaCalendarAlt,
  FaTimes,
  FaInfoCircle,
  FaStar,
  FaTrophy,
  FaBookmark,
  FaRocket,
  FaSearch,
  FaUserTie,
  FaBookOpen,
  FaComments,
  FaCheckCircle,
  FaClipboardCheck,
  FaFileCsv,
} from "react-icons/fa";
import TrainerService from "../../../services/TrainerDashboardService";
import { getMe } from "../../../services/AuthService";
import { toast } from '../common/Toast/toast';
import { confirm } from "../common/Confirm/confirm";
import { getTrainers } from "../../../services/TrainerService";
import "./TrainerDashboard.css";
import StudentDetailModal from "./StudentDetailModal.jsx";
import TeachingSummaryChart from "./TeachingSummaryChart.jsx";
import OnlineStudentsBox from "../common/OnlineStudents/OnlineStudentsBox.jsx";
/* OverlayTrigger/Tooltip are gone: react-bootstrap draws its tooltip
   with .tooltip-inner and .tooltip-arrow, both from the Bootstrap
   stylesheet this project deleted, so they rendered nothing. The kit's
   CSS tooltip (.pg-tip + data-tip) is used instead. */
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import ExportDialog from "../common/Export/ExportDialog";
/* .lp-tabs / .lp-tab -- the tab strip the detail popups share. */
import "../common/lms-pages.css";
import { useWaypoint } from "../common/waypoint/WaypointProvider.jsx";
import "../common/waypoint/waypoint.css";
import FeedbackList from "../Feedback/FeedbackList.jsx";
import MyFeedbackSummary from "../Feedback/MyFeedbackSummary.jsx";
import TrainerDashboardSkeleton from "./TrainerDashboardSkeleton.jsx";
import { silenceFor } from "../common/Loader/busy";
import CustomAsyncDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import TopbarSlot from "../../layouts/TopbarSlot";
import debounce from "lodash/debounce";
import { getMockInterviewEventStats } from "../../../services/MockInterviewListService";

// Mirrors the bands the /trainerdashboard/insights endpoint assigns, so the
// badge colour always agrees with the label the API sent.
// One label/value row. Renders nothing at all when the field is empty, so a
// batch missing half its optional fields does not show a column of "N/A".
const BatchDetail = ({ label, value, mono = false }) => {
  const text = String(value ?? "").trim();
  if (!text || text === "N/A" || text === "Unknown" || text === "Unknown Course") return null;
  return (
    <div className="detail-item">
      <span className="batch-detail-key">{label}</span>
      <span className={`batch-detail-value${mono ? " batch-detail-value--mono" : ""}`} title={text}>
        {text}
      </span>
    </div>
  );
};

// An event's `time` is stored as a UTC instant on 1970-01-01, so it has to be
// read back in UTC — reading it locally shifts every row by the browser offset.
const formatTimeUTC = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  const h = d.getUTCHours();
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${suffix}`;
};

// A mock interview can name a dozen batches and as many trainers. Past three
// the list gets its own scroll box rather than pushing the rest of the card
// off the screen — the count in the label says how many are in there, so the
// scrollbar is never the only clue that something is hidden.
const NameList = ({ label, names }) => {
  if (!names || names.length === 0) return null;
  const scrolls = names.length > 3;
  return (
    <div className="detail-item d-flex justify-content-between align-items-start mb-2">
      <span className="batch-detail-key">
        {label}
        {scrolls && <span className="name-list-count"> ({names.length})</span>}
      </span>
      <span className={`batch-detail-value name-list${scrolls ? " name-list--scroll" : ""}`}>
        {names.map((name) => (
          <span className="name-list-item" key={name} title={name}>
            {name}
          </span>
        ))}
      </span>
    </div>
  );
};

// First letter of the given name and of the surname. Falls back to the
// email's local part when there is no name, and to one letter for a single
// word, so the avatar is never empty.
const initialsOf = (name, email) => {
  const source = String(name || "").trim() || String(email || "").split("@")[0] || "";
  const parts = source.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

// A student keeps the same avatar colour across renders and pages, so the
// list stays recognisable while paging. Six tints, picked by name.
const AVATAR_TONES = 6;
const avatarTone = (seed) => {
  const t = String(seed || "");
  let h = 0;
  for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) % 100000;
  return h % AVATAR_TONES;
};

// Whether the reader has asked for less movement. Read at the moment of
// the scroll rather than once at mount, so changing the OS setting takes
// effect without a reload. Local, like initialsOf above: Notifications.jsx
// keeps its own copy of this for the same reason.
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Page numbers around the current one, with gaps standing in for the runs
// that are skipped: 1 … 7 8 [9] 10 11 … 42. Keeps the pager one line however
// many pages there are.
const pageWindow = (current, total, span = 1) => {
  if (total <= 1) return [1];
  const out = [];
  const push = (v) => {
    if (out[out.length - 1] !== v) out.push(v);
  };
  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || Math.abs(i - current) <= span) push(i);
    else push("gap");
  }
  return out;
};

// Batch names run long enough to break a table row. Cut at 15 characters
// and keep the full name on the title attribute so nothing is lost.
const ellipsis = (text, max = 15) => {
  const t = String(text ?? "");
  return t.length > max ? `${t.slice(0, max).trimEnd()}…` : t;
};

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_SHORT = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

// `days` reaches us in two forms: the batch schema defaults to "mon,tue,..."
// while the Add Batch form saves "monday,tuesday,...". Every reader in this
// file compared against the short keys, so long-form values matched nothing —
// the day filter returned no rows and the batch modal showed "N/A" for Days.
// Three letters is unambiguous across all seven, so trim and compare on that.
const normalizeDayKeys = (value) =>
  String(value || "")
    .split(/[,\s]+/)
    .map((d) => d.trim().toLowerCase().slice(0, 3))
    .filter((d) => DAY_SHORT[d]);

// "mon,tue,wed,thu,fri,sat,sun" is the schema default and means every day —
// worth saying so rather than listing all seven.
const formatDays = (value) => {
  const days = normalizeDayKeys(value);
  if (days.length === 0) return "";
  if (days.length === 7) return "Every day";
  return DAY_ORDER.filter((d) => days.includes(d)).map((d) => DAY_SHORT[d]).join(", ");
};

const PERFORMANCE_BAND_COLORS = {
  Excellent: "var(--teal)",
  Good: "var(--indigo)",
  "Needs attention": "var(--gold)",
  "At risk": "var(--berry)",
};

// The API grades the score (insights.performance.level: good / fair / poor);
// these only name the class each grade wears, so no threshold is restated in
// the browser.
const PERFORMANCE_TONE = { good: "teal", fair: "gold", poor: "berry" };
const PERFORMANCE_CHIP_TONE = { good: "good", fair: "", poor: "warn" };

const TrainerDashboard = () => {
  // Waypoint, so the people and batches this dashboard names can be
  // opened rather than only listed.
  const { open: openEntity } = useWaypoint();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Every tab but Overview scrolls. The flags are refs rather than state
  // because they are read inside a fetch that has already started: state
  // lags a frame, which is long enough for one pass to ask for two pages.
  const batchesEndRef = useRef(null);
  const studentsEndRef = useRef(null);
  const attendanceEndRef = useRef(null);
  const mockEndRef = useRef(null);
  // The tab strip, so a stat card can bring it into view -- see goToTab.
  const tabsRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Which half of the batch popup is showing. Reset per batch, not per
  // render, so opening a different batch always starts on Details.
  const [batchTab, setBatchTab] = useState("details");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [tabSearchTerms, setTabSearchTerms] = useState({
    overview: "",
    batches: "",
    students: "",
    classrooms: ""
  });
  const [trainerId, setTrainerId] = useState(null);
  const [trainerName, setTrainerName] = useState("");
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudentData, setSelectedStudentData] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [selectedTrainerOption, setSelectedTrainerOption] = useState(null);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [trainerPage, setTrainerPage] = useState(1);
  const [hasMoreTrainers, setHasMoreTrainers] = useState(true);
  const [trainerSearchTerm, setTrainerSearchTerm] = useState("");
  const isFetchingTrainersRef = useRef(false);


  const [tabPagination, setTabPagination] = useState({
    batches: { totalCount: 0, page: 1 },
    students: { totalCount: 0, page: 1 },
    classrooms: { totalCount: 0, page: 1 },
  });
  const [activeTabData, setActiveTabData] = useState({
    batches: [],
    students: [],
    classrooms: [],
  });
  const [isTabLoading, setIsTabLoading] = useState(false);
  // The Batches tab as the API split it: each state's rows, the count per
  // state and the dropdown options. Nothing here is recounted in the browser.
  const [batchLists, setBatchLists] = useState({ active: [], upcoming: [], completed: [] });
  const [batchCounts, setBatchCounts] = useState(null);
  const [batchOptions, setBatchOptions] = useState(null);
  // The overview's batch panels while a search is typed: the /mybatches
  // response the API narrowed. null means no search -- read dashboardData.
  const [overviewResult, setOverviewResult] = useState(null);
  const itemsPerPage = 25;

  const [batchFilter, setBatchFilter] = useState("");
  const [batchStatusFilter, setBatchStatusFilter] = useState("active");
  // Which tab's filter sheet is open on a phone. The batches and
  // attendance tabs carry a dozen controls each, which is a screenful
  // before the list starts; they collapse behind a button and open as
  // a sheet. The other two tabs have three or four and stay inline.
  const [filterSheet, setFilterSheet] = useState(null);

  // Row detail on phones. Lists drop columns as the viewport narrows, so a
  // tap opens what was dropped instead of making the table scroll for it.
  const [rowSheet, setRowSheet] = useState(null);

  // Which students are behind one of the Teaching Summary's counts.
  // { title, tone, rows } while open, null while closed.
  const [studentBand, setStudentBand] = useState(null);
  const isMobile = useIsMobile();

  // Every list calls this. On a phone it opens the sheet; on anything
  // wider it runs whatever the row already did — the batch modal, the
  // flagged-batch panel — so desktop behaviour is untouched.
  const openRow = (sheet, desktopAction) => {
    if (isMobile) setRowSheet(sheet);
    else if (desktopAction) desktopAction();
  }; // Added for Active/Completed sub-tabs

  // Every dimension a batch record can be narrowed by. All of it is sent to
  // /mybatches, which filters, sorts and splits by state; changing a filter
  // refetches.
  const EMPTY_BATCH_FILTERS = {
    course: "",
    mode: "",
    classroom: "",
    day: "",
    dateFrom: "",
    dateTo: "",
    timeFrom: "",
    timeTo: "",
    resource: "",
    minStudents: "",
    maxStudents: "",
    sort: "start-desc",
  };
  const [batchFilters, setBatchFilters] = useState(EMPTY_BATCH_FILTERS);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  // "Courses You Teach" shows five and expands in place — there is no courses
  // tab to send the trainer to.
  const [showAllCourses, setShowAllCourses] = useState(false);

  // ── Attendance tab ────────────────────────────────────────────────────────
  // Every filter is sent to the API, which owns the filtering, sorting and
  // paging — the browser never trims a page it was handed.
  const EMPTY_ATTENDANCE_FILTERS = {
    batchId: "",
    courseName: "",
    mode: "",
    batchStatus: "active",
    band: "",
    tail: "",
    minRate: "",
    maxRate: "",
    dateFrom: "",
    dateTo: "",
    search: "",
    sort: "rate-asc",
  };
  const [attendanceFilters, setAttendanceFilters] = useState(EMPTY_ATTENDANCE_FILTERS);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceOptions, setAttendanceOptions] = useState({ batches: [], courses: [], modes: [] });
  const [attendancePagination, setAttendancePagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Students tab date range (server-side, on the student's enrolment date).
  const [studentDateFrom, setStudentDateFrom] = useState("");
  const [studentDateTo, setStudentDateTo] = useState("");

  // Mock Interview tab
  const [mockDateFrom, setMockDateFrom] = useState("");
  const [mockDateTo, setMockDateTo] = useState("");
  const [mockTimeFrom, setMockTimeFrom] = useState("");
  const [mockTimeTo, setMockTimeTo] = useState("");
  const [mockList, setMockList] = useState([]);
  const [mockTotal, setMockTotal] = useState(0);
  const [mockPage, setMockPage] = useState(1);
  const [mockStats, setMockStats] = useState({});
  const [mockLoading, setMockLoading] = useState(false);
  const [mockSearch, setMockSearch] = useState("");
  const [mockWhen, setMockWhen] = useState("all");
  // The All / Upcoming / Past counts, from the API over the whole match.
  const [mockCounts, setMockCounts] = useState({ all: 0, upcoming: 0, past: 0 });

  // ✅ studentColumns using useMemo — recomputes whenever contactPersonMap changes
  const studentColumns = useMemo(
    () => [
      {
        title: "User",
        prop: "studentName",
        cell: (data) => {
          const name = data.studentName || data.name;
          const email = data.studentEmail || data.email;
          return (
            <div className="td-person">
              <span
                className={`td-person-avatar td-person-avatar--${avatarTone(name || email)}`}
                aria-hidden="true"
              >
                {initialsOf(name, email)}
              </span>
              <span className="td-person-id">
                <b className="td-person-name">{name || "—"}</b>
                <small className="td-person-mail">{email}</small>
              </span>
            </div>
          );
        },
      },
      {
        title: "Batch",
        prop: "batchName",
        cell: (data) => {
          let bName = data.batchName;
          if (!bName && Array.isArray(data.batchIds) && data.batchIds.length > 0) {
            bName = data.batchIds.map(b => (typeof b === "object" ? b.name : b)).filter(Boolean).join(", ");
          }
          return bName ? (
            <span className="td-tbl-pill td-tbl-pill--live">{bName}</span>
          ) : (
            <span className="td-person-none">—</span>
          );
        },
      },
      {
        title: "Mobile",
        prop: "studentNumber",
        cell: (data) => {
          const number = data.studentNumber || data.phone;
          return number ? (
            <a className="td-person-tel" href={`tel:${String(number).replace(/\s+/g, "")}`}>
              {number}
            </a>
          ) : (
            <span className="td-person-none">—</span>
          );
        },
      },
      {
        title: "Contact Person",
        prop: "contactPersonIds",
        cell: (data) => {
          const persons = data.contactPersonIds;
          if (!persons || !Array.isArray(persons) || persons.length === 0) return "—";

          // Get names from the objects provided by the API
          const names = persons
            .map(p => typeof p === 'object' ? p.name : p)
            .filter(Boolean);

          return names.length > 0 ? (
            <span className="td-person-contact">{names.join(", ")}</span>
          ) : (
            <span className="td-person-none">—</span>
          );
        },
      },
      {
        /* Fifth and last -- list_page_rules caps a list at five columns,
           and /all-students ends on this same pill, so the two student
           lists read alike.

           Course is deliberately NOT a column here: /trainerdashboard/
           mystudents enriches batchIds and contactPersonIds only
           (BATCH_STUDENT_CONFIG), so courseIds would print raw ObjectIds
           rather than course names the way /all-students can. */
        title: "Verified",
        prop: "isVerifiedbyCounsellor",
        cell: (data) => (
          <span
            className={`pg-pill ${
              data.isVerifiedbyCounsellor ? "pg-pill--good" : "pg-pill--warn"
            }`}
          >
            {data.isVerifiedbyCounsellor ? "Verified" : "Unverified"}
          </span>
        ),
      },
    ],
    [],
  );

  // const classroomColumns = [
  //   {
  //     title: "Classroom No",
  //     prop: "classroomNo",
  //     cell: (data) => (
  //       <div className="classroom-info">
  //         <div className="classroom-icon">
  //           <FaMapMarkerAlt />
  //         </div>
  //         <span className="fw-bold">{data.classroomNo || data.id}</span>
  //       </div>
  //     ),
  //   },
  //   {
  //     title: "Batch",
  //     prop: "batchName",
  //     cell: (data) => (
  //       <Badge bg="primary" className="batch-badge">
  //         {data.batchName || "N/A"}
  //       </Badge>
  //     ),
  //   },
  //   {
  //     title: "Schedule",
  //     prop: "startTime",
  //     cell: (data) => (
  //       <div className="text-black">
  //         <small className="d-block">
  //           <FaCalendarAlt className="me-1" />
  //           {data.startTime ? formatDate(data.startTime) : "N/A"}
  //         </small>
  //         <small className="d-block mt-1">
  //           <FaClock className="me-1" />
  //           {data.startTime ? formatTime(data.startTime) : "N/A"} -{" "}
  //           {data.endTime ? formatTime(data.endTime) : "N/A"}
  //         </small>
  //       </div>
  //     ),
  //   },
  //   {
  //     title: "Status",
  //     prop: "status",
  //     cell: (data) => getStatusBadge(data.status || "Available"),
  //   },
  //   {
  //     title: "Description",
  //     prop: "description",
  //     cell: (data) => (
  //       <small
  //         className="text-muted d-block text-wrap"
  //         style={{ maxWidth: "200px" }}
  //       >
  //         {data.description || "No description provided"}
  //       </small>
  //     ),
  //   },
  // ];

  const loadTabData = useCallback(
    async (tableKey, page, search = "") => {
      try {
        // Page 1 is a fresh load -- a filter applied, a tab opened -- and the
        // page says it is working. Past that the request came from the
        // scroll: the rows on screen stay and the batch lands under them, so
        // neither the full-page loader nor the table's overlay may appear.
        // The foot's own spinner (loadingMore) is the indicator for that.
        if (page <= 1) setIsTabLoading(true);
        const params = {
          page,
          limit: itemsPerPage,
          search: search,
        };

        let response;
        if (tableKey === "batches") {
          // Every filter goes to the API — the counts shown are the counts the
          // database holds, not a subset of one page narrowed in the browser.
          response = await TrainerService.getBatchesByTrainer(trainerId, {
            ...params,
            courseName: batchFilters.course || undefined,
            mode: batchFilters.mode || undefined,
            classroom: batchFilters.classroom || undefined,
            day: batchFilters.day || undefined,
            dateFrom: batchFilters.dateFrom || undefined,
            dateTo: batchFilters.dateTo || undefined,
            timeFrom: batchFilters.timeFrom || undefined,
            timeTo: batchFilters.timeTo || undefined,
            resource: batchFilters.resource || undefined,
            minStudents: batchFilters.minStudents || undefined,
            maxStudents: batchFilters.maxStudents || undefined,
            sort: batchFilters.sort || undefined,
          });
        } else if (tableKey === "students") {
          response = await TrainerService.getStudentsByTrainer(trainerId, {
            ...params,
            batchId: batchFilter || null,
            dateFrom: studentDateFrom || undefined,
            dateTo: studentDateTo || undefined,
          });
        } /* else if (tableKey === "classrooms") {
          response = await TrainerService.getClassroomsByTrainer(trainerId, params);
        } */

        const items = response?.data || [];
        const total = response?.totalCount || response?.pagination?.total || 0;

        if (tableKey === "batches" && response) {
          const lists = {
            active: response.activeBatches || [],
            upcoming: response.upcomingBatches || [],
            completed: response.completedBatches || [],
          };
          // A later page only ever adds rows the list has not seen.
          setBatchLists((prev) => {
            if (page <= 1) return lists;
            const merge = (have, next) => {
              const seen = new Set(have.map((r) => r.id));
              return [...have, ...next.filter((r) => !seen.has(r.id))];
            };
            return {
              active: merge(prev.active, lists.active),
              upcoming: merge(prev.upcoming, lists.upcoming),
              completed: merge(prev.completed, lists.completed),
            };
          });
          setBatchCounts(response.counts || null);
          setBatchOptions(response.filterOptions || null);
        }

        setTabPagination((prev) => ({
          ...prev,
          [tableKey]: {
            ...prev[tableKey],
            totalCount: total,
            totalPages: response?.pagination?.totalPages || Math.ceil(total / itemsPerPage)
          },
        }));

        setActiveTabData((prev) => {
          if ((tableKey === "batches" || tableKey === "students") && page > 1) {
            const seen = new Set(prev[tableKey].map((r) => r.id));
            return {
              ...prev,
              [tableKey]: [
                ...prev[tableKey],
                ...items.filter((r) => !seen.has(r.id)),
              ],
            };
          }
          return {
            ...prev,
            [tableKey]: items,
          };
        });
      } catch (err) {
        console.error(`Error loading ${tableKey} data:`, err);
      } finally {
        setIsTabLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [trainerId, itemsPerPage, batchFilter, batchFilters, studentDateFrom, studentDateTo],
  );

  const handlePageChange = useCallback((tableKey, page) => {
    setTabPagination((prev) => ({
      ...prev,
      [tableKey]: { ...prev[tableKey], page },
    }));
  }, []);

  /* The foot of whichever tab is showing asks for its next page when it
     comes into view. Overview has no list of its own, so it has no sentinel.

     `busy` MUST be a flag that is raised for every page, including the
     appended ones -- which is what loadingMore is, and what the per-tab
     loading flags are not: every loader here raises those only for a fresh
     load (`if (page <= 1)` / `if (!append)`). That matters because an
     IntersectionObserver fires on a CHANGE of intersection. If the effect
     does not re-run when a page lands, the observer created before it is
     still mounted, the sentinel never left the viewport, and nothing ever
     fires again -- the list simply stops. Re-running on busy: true -> false
     builds a fresh observer, which reports its target's current state
     immediately, so a foot still in view asks for the next page. */
  const useTabScroll = (ref, enabled, hasMore, busy, next) => {
    useEffect(() => {
      const node = ref.current;
      if (!node || !enabled || !hasMore || busy) return undefined;
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          // `busy` is state and lags a frame, so the ref is what stops one
          // pass asking for two pages.
          if (loadingMoreRef.current) return;
          loadingMoreRef.current = true;
          setLoadingMore(true);
          next();
        },
        { rootMargin: "150px" },
      );
      io.observe(node);
      return () => io.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, hasMore, busy, next]);
  };

  const handleLoadMoreBatches = useCallback(() => {
    const nextPage = tabPagination.batches.page + 1;
    handlePageChange("batches", nextPage);
  }, [tabPagination.batches.page, handlePageChange]);

  const handleSearch = useCallback((tabKey, searchValue) => {
    const term = typeof searchValue === "object" ? searchValue.term || "" : searchValue;

    setTabSearchTerms((prev) => ({
      ...prev,
      [tabKey]: term
    }));

    setTabPagination((prev) => {
      if (prev[tabKey]) {
        return {
          ...prev,
          [tabKey]: { ...prev[tabKey], page: 1 }
        };
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (activeTab === "overview" || loading || !trainerId) return;

    const apiTabMap = {
      batches: "batches",
      students: "students",
      classrooms: "classrooms",
    };

    const targetTab = apiTabMap[activeTab];
    if (targetTab) {
      const page = tabPagination[targetTab]?.page || 1;
      const currentTabSearch = tabSearchTerms[targetTab] || "";

      const timeoutId = setTimeout(() => {
        loadTabData(targetTab, page, currentTabSearch);
      }, 500);

      return () => clearTimeout(timeoutId);

    }
  }, [
    activeTab,
    tabPagination.batches.page,
    tabPagination.students.page,
    tabPagination.classrooms.page,
    tabSearchTerms.batches,
    tabSearchTerms.students,
    tabSearchTerms.classrooms,
    loading,
    trainerId,
    // loadTabData closes over the filter state, so changing any filter
    // re-runs this and refetches from the server.
    loadTabData,
    batchFilter,
  ]);

  // Mock interviews are events with category "Mock Interview"; the listing and
  // the turnout roll-up are two calls, kept separate so a slow stats query
  // never holds the schedule back.
  const loadMockInterviews = useCallback(async (page = 1, append = false) => {
    if (!trainerId) return;
    if (!append) setMockLoading(true);
    try {
      // The When segment, its three counts and the date order are the API's;
      // the rows arrive already narrowed and ordered.
      const res = await TrainerService.getTrainerMockInterviews(trainerId, {
        limit: 25,
        page,
        search: mockSearch,
        when: mockWhen,
        dateFrom: mockDateFrom,
        dateTo: mockDateTo,
        timeFrom: mockTimeFrom,
        timeTo: mockTimeTo,
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setMockTotal(Number(res?.pagination?.total || 0));
      setMockCounts(res.counts);
      setMockPage(page);
      if (append) {
        setMockList((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...rows.filter((r) => !seen.has(r.id))];
        });
      } else {
        setMockList(rows);
      }

      // Stats are merged by id, so a page appended later lands correctly.
      const ids = rows.map((r) => r.id).filter(Boolean);
      if (ids.length) {
        const stats = await getMockInterviewEventStats(ids);
        setMockStats((prev) => (append ? { ...prev, ...stats } : stats));
      } else if (!append) {
        setMockStats({});
      }
    } catch (err) {
      console.error("Error loading mock interviews:", err);
      toast.error("Failed to load mock interviews");
      setMockList([]);
      setMockStats({});
      setMockCounts({ all: 0, upcoming: 0, past: 0 });
    } finally {
      setMockLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [trainerId, mockSearch, mockWhen, mockDateFrom, mockDateTo, mockTimeFrom, mockTimeTo]);

  useEffect(() => {
    if (activeTab !== "mockinterviews" || !trainerId) return;
    const timeoutId = setTimeout(loadMockInterviews, 400);
    return () => clearTimeout(timeoutId);
  }, [activeTab, trainerId, loadMockInterviews]);

  // The overview's search goes to the API like every other list here. The
  // panels keep the last result on screen while the next one is on its way.
  useEffect(() => {
    const term = tabSearchTerms.overview.trim();
    if (!term || !trainerId) {
      setOverviewResult(null);
      return undefined;
    }
    let stale = false;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await TrainerService.getBatchesByTrainer(trainerId, {
          page: 1,
          limit: 200,
          search: term,
        });
        if (!stale) setOverviewResult(res);
      } catch (err) {
        console.error("Error searching overview batches:", err);
      }
    }, 400);
    return () => {
      stale = true;
      clearTimeout(timeoutId);
    };
  }, [tabSearchTerms.overview, trainerId]);

  // ── Attendance roster ─────────────────────────────────────────────────────
  const loadAttendanceRoster = useCallback(
    async (page = 1, append = false) => {
      if (!trainerId) return;
      if (!append) setAttendanceLoading(true);
      try {
        const res = await TrainerService.getAttendanceRoster({
          ...attendanceFilters,
          trainerId,
          page,
          limit: attendancePagination.limit,
        });
        if (append) {
          setAttendanceRows((prev) => {
            const seen = new Set(prev.map((r) => r.studentId || r.id));
            return [
              ...prev,
              ...(res.rows || []).filter((r) => !seen.has(r.studentId || r.id)),
            ];
          });
        } else {
          setAttendanceRows(res.rows);
        }
        setAttendanceSummary(res.summary);
        // Options describe every batch the trainer owns, so they must not be
        // overwritten with an empty set when a filter matches nothing.
        if (res.filterOptions?.batches?.length) setAttendanceOptions(res.filterOptions);
        setAttendancePagination((prev) => ({ ...prev, ...(res.pagination || {}), page }));
      } catch (err) {
        console.error("Error loading attendance roster:", err);
        toast.error("Failed to load attendance");
        setAttendanceRows([]);
        setAttendanceSummary(null);
      } finally {
        setAttendanceLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trainerId, attendanceFilters, attendancePagination.limit]
  );

  useEffect(() => {
    if (activeTab !== "attendance" || !trainerId) return;
    // Debounced so typing in the search box is one request, not one per key.
    const timeoutId = setTimeout(() => loadAttendanceRoster(1), 350);
    return () => clearTimeout(timeoutId);
  }, [activeTab, trainerId, loadAttendanceRoster]);

  const nextStudentsPage = useCallback(
    () => handlePageChange("students", tabPagination.students.page + 1),
    [handlePageChange, tabPagination.students.page],
  );

  const nextAttendancePage = useCallback(
    () => loadAttendanceRoster(attendancePagination.page + 1, true),
    [loadAttendanceRoster, attendancePagination.page],
  );

  const nextMockPage = useCallback(
    () => loadMockInterviews(mockPage + 1, true),
    [loadMockInterviews, mockPage],
  );

  useTabScroll(
    batchesEndRef,
    activeTab === "batches",
    activeTabData.batches.length < tabPagination.batches.totalCount,
    isTabLoading || loadingMore,
    handleLoadMoreBatches,
  );

  useTabScroll(
    studentsEndRef,
    activeTab === "students",
    activeTabData.students.length < tabPagination.students.totalCount,
    isTabLoading || loadingMore,
    nextStudentsPage,
  );

  useTabScroll(
    attendanceEndRef,
    activeTab === "attendance",
    attendanceRows.length < attendancePagination.total,
    attendanceLoading || loadingMore,
    nextAttendancePage,
  );

  useTabScroll(
    mockEndRef,
    activeTab === "mockinterviews",
    mockList.length < mockTotal,
    mockLoading || loadingMore,
    nextMockPage,
  );

  const setAttendanceFilter = (key, value) =>
    setAttendanceFilters((prev) => ({ ...prev, [key]: value }));

  const attendanceFilterCount = Object.entries(attendanceFilters).filter(
    ([k, v]) => v !== "" && v !== EMPTY_ATTENDANCE_FILTERS[k]
  ).length;

  /*
   * The attendance tab's columns, for the shared export popup. The API
   * owns the filtering, so what downloads is exactly what is on screen
   * -- no second, differently-filtered call.
   *
   * The hand-rolled CSV that used to sit here quoted every field
   * unconditionally, wrote no BOM (so Excel read a name with an accent
   * as the system codepage) and said nothing at all afterwards.
   */
  const attendanceExportColumns = [
    { key: "name", label: "Student", value: (r) => r.name },
    { key: "email", label: "Email", value: (r) => r.email },
    { key: "phone", label: "Phone", value: (r) => r.phone },
    { key: "batchName", label: "Batch", value: (r) => r.batchName },
    { key: "courseName", label: "Course", value: (r) => r.courseName },
    { key: "mode", label: "Mode", value: (r) => r.mode },
    { key: "sessions", label: "Sessions", value: (r) => r.sessions },
    { key: "attended", label: "Attended", value: (r) => r.attended },
    { key: "absent", label: "Absent", value: (r) => r.absent },
    {
      key: "rate",
      label: "Attendance %",
      value: (r) => (r.rate === null ? "" : r.rate),
    },
    {
      key: "lastPresentAt",
      label: "Last present",
      value: (r) => (r.lastPresentAt ? formatDate(r.lastPresentAt) : ""),
    },
  ];

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      initializeDashboard();
      hasFetched.current = true;
    }
  }, []);

  const handleStudentClick = (student, batchId, batchName) => {
    setSelectedStudentData({
      student: student,
      batchId: batchId,
      batchName: batchName || student.batchName,
      trainerId: trainerId
    });
    setShowStudentModal(true);
  };

  const fetchTrainers = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingTrainersRef.current = true;
      const resp = await getTrainers(10, pageNum, search);
      const trainersList = resp.data || [];
      const hasMore = trainersList.length === 10;
      return { trainers: trainersList, hasMore };
    } catch (err) {
      console.error("Error fetching trainers:", err);
      return { trainers: [], hasMore: false };
    } finally {
      isFetchingTrainersRef.current = false;
    }
  }, []);

  const loadTrainers = useCallback(async (search = "", pageNum = 1, append = false) => {
    setLoadingTrainers(true);
    const { trainers, hasMore } = await fetchTrainers(search, pageNum);

    const newOptions = trainers
      .map((t) => {
        const id = t.id || t.trainerId || t.userId;
        const name = t.trainerName || t.name || "";
        const email = t.trainerEmail || t.email || "";
        return {
          value: id,
          label: email ? `${name} | ${email}` : name,
          data: t,
        };
      })
      .filter((x) => x.value);

    if (append) {
      setTrainerOptions((prev) => [...prev, ...newOptions]);
    } else {
      setTrainerOptions(newOptions);
    }

    setHasMoreTrainers(hasMore);
    setLoadingTrainers(false);
  }, [fetchTrainers]);

  const handleTrainerSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setTrainerSearchTerm(inputValue);
        setTrainerPage(1);
        await loadTrainers(inputValue, 1, false);
      }, 700),
    [loadTrainers]
  );

  const handleLoadMoreTrainers = useCallback(async () => {
    if (!hasMoreTrainers || loadingTrainers || isFetchingTrainersRef.current) return;
    const nextPage = trainerPage + 1;
    setTrainerPage(nextPage);
    await loadTrainers(trainerSearchTerm, nextPage, true);
  }, [hasMoreTrainers, loadingTrainers, trainerPage, trainerSearchTerm, loadTrainers]);

  const handleDropdownOpen = useCallback(async () => {
    if (isAdmin && trainerOptions.length === 0 && !loadingTrainers) {
      await loadTrainers("", 1, false);
    }
  }, [isAdmin, trainerOptions.length, loadingTrainers, loadTrainers]);

  const handleRefreshTrainers = useCallback(async () => {
    setTrainerSearchTerm("");
    setTrainerPage(1);
    await loadTrainers("", 1, false);
  }, [loadTrainers]);

  const initializeDashboard = async () => {
    try {
      setLoading(true);
      // The skeleton is this load's indicator, so the global overlay stays
      // down for it (Loader/busy.js). Actions still raise the overlay.
      silenceFor();
      const userProfile = await getMe();

      const userData = userProfile.data;
      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser = profileLabel === "admin" || userData.isSuperAdmin;

      setIsAdmin(isAdminUser);

      if (isAdminUser) {
        setLoading(false);
        return;
      }

      const userId = userData.id || userData._id || userData.userId;
      const trainerNameValue = `${userData.firstName || ""} ${userData.lastName || ""
        }`.trim();

      if (!userId) {
        setError("Unable to fetch trainer information");
        return;
      }

      let actualTrainerId = userId;
      if (isTrainerUser) {
        try {
          silenceFor();
          const trainerResp = await TrainerService.getTrainerByTrainerId(userId);
          if (trainerResp && trainerResp.data) {
            actualTrainerId = trainerResp.data.id || trainerResp.data._id;
          }
        } catch (err) {
          console.error("Error fetching trainer profile:", err);
        }
      }

      setTrainerId(actualTrainerId);
      setTrainerName(trainerNameValue);
      await fetchDashboardData(actualTrainerId);
    } catch (err) {
      console.error("Error in initializeDashboard:", err);
      setError("Failed to initialize dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async (id) => {
    try {
      // Shown as the skeleton, not the overlay -- see initializeDashboard.
      silenceFor();
      const data = await TrainerService.getTrainerDashboardData(id);
      setDashboardData(data);
      // todaySchedule is commented out because it hits the classrooms API
      // await fetchTodaySchedule(id);
    } catch (err) {
      setError("Failed to load dashboard data");
      console.error(err);
    }
  };


  const handleTrainerSelection = async (selectedOption) => {
    if (!selectedOption) {
      handleClearSelection();
      return;
    }

    setSelectedTrainerOption(selectedOption);
    setLoading(true);
    setError(null);

    try {
      const selectedTrainerId = selectedOption.value;
      const trainerData = selectedOption.data;
      const selectedTrainerName =
        trainerData?.trainerName || selectedOption.label || "Unknown Trainer";

      setTrainerId(selectedTrainerId);
      setTrainerName(selectedTrainerName);

      await fetchDashboardData(selectedTrainerId);
    } catch (err) {
      console.error("Error loading trainer dashboard:", err);
      setError("Failed to load selected trainer's dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedTrainerOption(null);
    setDashboardData(null);
    setTrainerId(null);
    setTrainerName("");
    setTrainerPage(1);
    setTrainerSearchTerm("");
  };

  const handleBatchClick = async (batch) => {
    setSelectedBatch(batch);
    setBatchTab("details");
    setShowBatchModal(true);
    setBatchStudents([]);
    setIsTabLoading(true);

    try {
      const batchId = batch.id || batch._id;
      const studentsResp = await TrainerService.getStudentsByTrainer(trainerId, {
        batchId,
        limit: 1000
      });
      setBatchStudents(studentsResp.data || []);
    } catch (error) {
      console.error("Failed to fetch batch students:", error);
      toast.error("Failed to load students for this batch");
    } finally {
      setIsTabLoading(false);
    }
  };

  /* The course a batch belongs to, whatever shape the endpoint used.
     Some hand back a populated courseId, some a bare string, some put
     it under courseDetails -- and Waypoint needs an id, not a name. */
  const courseIdOf = (batch) => {
    const c = batch?.courseId ?? batch?.courseDetails ?? batch?.course;
    if (!c) return null;
    if (typeof c === "string") return c;
    const one = Array.isArray(c) ? c[0] : c;
    return one?.id || one?._id || null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // The tone is a class, not a colour: the palette lives in
  // TrainerDashboard.css so it follows the theme.
  const getModeBadge = (mode) => (
    <span className={`td-chip td-chip--${String(mode || "other").toLowerCase()}`}>
      {mode}
    </span>
  );

  // Batch times are stored as full datetimes; only the clock part is wanted
  // when saying when today's class runs.
  const formatTimeOfDay = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const daysUntil = (value) => {
    const target = new Date(value);
    if (Number.isNaN(target.getTime())) return "";
    // Compare calendar days, not elapsed milliseconds. A batch ending at 18:00
    // today is 0.4 days away, which Math.ceil rounded up to 1 — so the row read
    // "Ends 3 Sept · tomorrow" on the very day it was ending.
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const days = Math.round(
      (startOfDay(target) - startOfDay(new Date())) / 86400000
    );
    if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    return `in ${days} days`;
  };

  // What each Batches-tab dropdown offers, from the API: every value across
  // the trainer's batches. The dashboard payload carries the same set, so the
  // dropdowns are filled before the tab's own request lands.
  const batchFilterOptions =
    batchOptions || dashboardData?.batchFilterOptions || { courses: [], modes: [], classrooms: [] };

  const activeBatchFilterCount = useMemo(
    () =>
      Object.entries(batchFilters).filter(
        ([key, value]) => key !== "sort" && String(value || "").trim() !== ""
      ).length,
    [batchFilters]
  );

  const updateBatchFilter = useCallback((key, value) => {
    setBatchFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  /* The Teaching Summary chips are counts; the question they prompt is
     always "which students?". This used to answer it with a SweetAlert
     built from an HTML string -- a table of names that reached none of
     them, because a string cannot carry a click handler. It is the
     house popup now, and every name is a Waypoint. */
  const showStudentBand = (title, list, tone) => {
    setStudentBand({ title, tone, rows: Array.isArray(list) ? list : [] });
  };

  // Attendance and days-left used to sit in the row. They are the detail
  // behind the flag rather than the flag itself, so they live here now and
  // the row carries only the batch and why it was flagged.
  /* The house confirm, as plain lines. This was a SweetAlert built from an
     HTML string of inline styles on the retired --ink/--line layer with a
     saffron confirm button; confirm() draws text, so the same facts are
     said as lines under the batch's name. */
  const showFlaggedBatch = (batch, s, reasons) => {
    const rate = s.attendanceRate;
    const daysLeft =
      s.daysRemaining !== null && s.daysRemaining >= 0
        ? `${s.daysRemaining} day${s.daysRemaining === 1 ? "" : "s"}`
        : "Ended";

    const lines = [
      batch.courseName || "",
      `Attendance: ${rate === null ? "—" : `${rate}%`} (${
        s.sessions
          ? `${s.presentMarks} present of ${s.expectedMarks} across ${s.sessions} session${s.sessions === 1 ? "" : "s"}`
          : "no sessions marked yet"
      })`,
      `Days left: ${daysLeft} (${
        s.syllabusProgress === null
          ? "no syllabus data"
          : `syllabus ${s.syllabusProgress}% · timeline ${s.elapsedProgress}%`
      })`,
      "",
      "Why it is flagged:",
      ...reasons.map((r) => `• ${r.label} — ${r.detail}`),
    ];

    confirm({
      title: batch.name || "Flagged batch",
      text: lines.filter((line, i) => line || i > 0).join("\n"),
      confirmButtonText: "Open batch",
      cancelButtonText: "Close",
    }).then((res) => {
      if (res.isConfirmed) handleBatchClick(batch);
    });
  };

  // Defined here so the "pick a trainer" empty state and the loaded
  // dashboard mount the same control. It is a portal, so it re-renders
  // with this component and never holds a stale copy of its state.
  const adminTrainerPicker = (
    <TopbarSlot>
      <div className="smt-tb-admin">
        <span className="smt-tb-admin-label">
          <FaUserTie size={14} />
          Admin View
        </span>
        {/* CustomAsyncDropdown never forwards `className` to its root
            (it only reads `rest.style`), so the hook for sizing it down
            to the bar's 40px has to be a wrapper. */}
        <div className="smt-tb-admin-select">
          <CustomAsyncDropdown
            options={trainerOptions}
            value={selectedTrainerOption ? [selectedTrainerOption] : []}
            onChange={(selectedOptions) => {
              const opt = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
              handleTrainerSelection(opt);
            }}
            placeholder="Select trainer to view dashboard..."
            isMulti={false}
            isSearchable={true}
            isLoading={loadingTrainers}
            onInputChange={(val) => {
              if (val) handleTrainerSearch(val);
            }}
            onMenuScrollToBottom={handleLoadMoreTrainers}
            onMenuOpen={handleDropdownOpen}
            onRefresh={handleRefreshTrainers}
          />
        </div>
      </div>
    </TopbarSlot>
  );

  if (loading) {
    /* The dashboard's own shape, shimmering, while its data is on the way
       -- inside the same .dashboard-wrapper > .td-page as the real page,
       which is what scopes the frames it borrows. The admin's trainer
       picker stays in the top bar through the load. */
    return (
      <div className="dashboard-wrapper">
        <Page>
          {isAdmin && adminTrainerPicker}
          <TrainerDashboardSkeleton />
        </Page>
      </div>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="td-error" role="alert">
          <div className="td-error-row">
            <div className="td-error-icon">
              <FaInfoCircle size={24} />
            </div>
            <div>
              <h4 className="td-error-title">Dashboard Temporarily Unavailable</h4>
              <p className="td-error-text">{error}</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }



  if (isAdmin && !selectedTrainerOption) {
    return (
      <Page className="td-space-lg">
        {adminTrainerPicker}
        <div className="td-pick">
          <div className="td-pick-body">
            {/* 22, not 32: the disc is 44px now, matching
                /student-profile's gate. */}
            <div className="td-pick-mark">
              <FaUserTie size={22} />
            </div>
            <h3 className="td-pick-title">Admin - Trainer Management</h3>
            <p className="td-pick-sub">
              Select a trainer to view their dashboard and manage their students.
            </p>

            <div className="td-pick-field">
              <CustomAsyncDropdown
                options={trainerOptions}
                value={selectedTrainerOption ? [selectedTrainerOption] : []}
                onChange={(selectedOptions) => {
                  const opt = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
                  handleTrainerSelection(opt);
                }}
                placeholder="Search by trainer name or email..."
                isMulti={false}
                isSearchable={true}
                isLoading={loadingTrainers}
                onInputChange={(val) => {
                  if (val) handleTrainerSearch(val);
                }}
                onMenuScrollToBottom={handleLoadMoreTrainers}
                onMenuOpen={handleDropdownOpen}
                onRefresh={handleRefreshTrainers}
              />
            </div>

            <p className="td-pick-tip">
              <FaInfoCircle />
              Tip: Once a trainer is selected, you can view their active batches and
              specific student attendance records.
            </p>
          </div>
        </div>
      </Page>
    );
  }

  const { trainer, batches, insights } = dashboardData || {};

  // Everything the page prints about its batches is decided by the API:
  // which meet today and which need attention come from /insights; the
  // overview's three panels and their totals from /batches, or from the
  // search result while one is typed.
  const todayClasses = insights?.todayClasses || [];
  const needsAttention = insights?.needsAttention || [];
  const overview = overviewResult
    ? {
        active: overviewResult.activeBatches || [],
        upcoming: overviewResult.upcomingBatches || [],
        completed: overviewResult.completedBatches || [],
        summary: overviewResult.summary || {},
      }
    : {
        active: dashboardData?.activeBatches || [],
        upcoming: dashboardData?.upcomingBatches || [],
        completed: dashboardData?.completedBatches || [],
        summary: dashboardData?.batchSummary || {},
      };

  // Overall performance for the identity panel's ring — the same 0-100
  // composite the "Performance Score" vital shows, clamped by /insights.
  const perfScore = insights?.performance?.score ?? 0;
  // Speedometer geometry: a half-circle of radius 80 swept left-to-right
  // over the top, so the arc length is half a circumference.
  const GAUGE_R = 80;
  const GAUGE_ARC = `M 20 100 A ${GAUGE_R} ${GAUGE_R} 0 0 1 180 100`;
  const GAUGE_LEN = Math.PI * GAUGE_R;

  return (
    <div className="dashboard-wrapper">
      {/* Applying a filter reloads the tab's whole table, so the page
          says it is working rather than leaving the old rows looking
          current. */}

      <Page>
        {/* Admin's trainer picker — mounted into the topbar, in the
            space the global search normally occupies, rather than
            costing a full-width card at the top of the dashboard. */}
        {isAdmin && adminTrainerPicker}

        {/* Identity and figures share one row: who the dashboard is for on
            the left, the numbers gridded beside it. */}
        <Grid space="md" className="sm-dash-top">
          <Cell xl={5} lg={6}>
            <div className="hero h-100">
              <div className="hero-grid"></div>
              <div className="hero-inner">
                <div className="hero-text">
                  <div className="eyebrow">
                    <FaStar />
                    {isAdmin
                      ? "Admin Dashboard - Trainer View"
                      : "Premium Trainer Dashboard"}
                  </div>
                  <h1>
                    {isAdmin ? (
                      <>Viewing <span>{trainerName}</span>'s Dashboard</>
                    ) : (
                      <>Welcome Back, <span>{trainerName}</span>!</>
                    )}
                  </h1>
                  <p>
                    <FaGraduationCap />
                    Specialization: {trainer?.specialization || "N/A"}
                    {trainer?.experience && (
                      <>
                        {" "}&nbsp;•&nbsp; Experience: {trainer.experience}
                      </>
                    )}
                  </p>
                  {/* Overall performance as a speedometer. The panel is the
                      one place on the page that speaks about the trainer
                      rather than their batches, so the composite score sits
                      here — above the quote, which closes the block. */}
                  <div
                    className="hero-gauge"
                    role="img"
                    aria-label={`Overall performance ${insights ? perfScore : 0} out of 100`}
                  >
                    <svg viewBox="0 0 200 116" aria-hidden="true">
                      <path
                        className="hero-gauge-track"
                        d={GAUGE_ARC}
                      />
                      <path
                        className="hero-gauge-fill"
                        d={GAUGE_ARC}
                        strokeDasharray={GAUGE_LEN}
                        strokeDashoffset={GAUGE_LEN * (1 - perfScore / 100)}
                      />
                    </svg>
                    <div className="hero-gauge-face">
                      <b>{insights ? perfScore : "—"}</b>
                      <small>{insights?.performance?.band || "Performance"}</small>
                    </div>
                    <div className="hero-gauge-scale" aria-hidden="true">
                      <span>0</span>
                      <span>100</span>
                    </div>
                  </div>
                  {/* Daily motivation, rotating. Sits under the identity
                      line so the panel says who you are, then gives you
                      something to start on. */}
                  <QuoteTicker />
                </div>

              </div>
            </div>
          </Cell>

        {/* Chat — AI assistant + every trainer, switchable in one page — temporarily disabled, do not remove.
        <Grid space="md">
          <Cell>
            <div className="sd-assistant-banner">
              <div className="p-4 d-flex flex-column flex-md-row align-items-center justify-content-between">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  <div className="bg-white p-1 rounded-circle me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '54px', height: '54px', flexShrink: 0 }}>
                    <FaComments size={24} color="var(--teal)" />
                  </div>
                  <div className="text-start">
                    <h4 className="sd-assistant-title">Chat</h4>
                    <p className="sd-assistant-sub">Ask the AI assistant about courses or batches, or message any trainer directly and see who's online, all in one place.</p>
                  </div>
                </div>
                <button
                  className="sd-assistant-btn"
                  onClick={() => navigate('/assistant')}
                >
                  Start Chat
                </button>
              </div>
            </div>
          </Cell>
        </Grid>
        */}

        {/* Stats — the same card, one per figure the trainer actually acts on.
            `chips` is for a figure made of parts (mode mix, strong vs at risk):
            three short chips scan far better than one run-on line. `hint` is
            for a figure with a single supporting fact. */}
          <Cell xl={7} lg={6}>
            <div className="sm-vital-grid">
          {/* Every figure, chip count and tone below is sent by /insights.
              The cards used to fall back to counting the batch list in the
              browser when a figure was missing, which could put two
              different answers to "how many are running" side by side; a
              figure the API did not send now reads "—". The `meter` each
              card once carried is gone too -- nothing drew it. */}
          {[
            {
              icon: <FaGraduationCap size={22} />,
              value: insights ? insights.batches.total : "—",
              label: "Total Batches",
              goTab: 'batches',
              tone: "sky",
              chips: (insights?.batches?.modes || []).map(({ mode, count }) => ({
                text: `${count} ${mode}`,
                tone: "quiet",
              })),
              hint: insights ? "No batches yet" : "Loading",
            },
            {
              icon: <FaUsers size={22} />,
              value: insights ? insights.students.total : "—",
              label: "Total Students",
              goTab: 'students',
              tone: "indigo",
              chips: insights
                ? [
                    { text: `${insights.students.strong} attending well`, tone: "good" },
                    { text: `${insights.students.atRisk} at risk`, tone: "warn" },
                  ]
                : [],
              hint: "Loading",
            },
            {
              icon: <FaRocket size={22} />,
              value: insights ? insights.batches.active : "—",
              label: "Running Now",
              goTab: 'batches',
              tone: "teal",
              chips: insights
                ? [
                    { text: `${insights.batches.upcoming} upcoming`, tone: "quiet" },
                    { text: `${insights.batches.completed} completed`, tone: "quiet" },
                  ]
                : [],
              hint: "Loading",
            },
            {
              icon: <FaCheckCircle size={22} />,
              // Students present ÷ students enrolled, across every session
              // this trainer has marked.
              value: insights ? `${insights.attendance.rate}%` : "—",
              label: "Attendance Rate",
              goTab: 'attendance',
              tone: insights?.attendance?.healthy ? "teal" : "gold",
              hint: insights
                ? `${insights.attendance.sessionsConducted} sessions marked`
                : "Loading",
              chips: insights?.attendance?.last30Days?.sessionsConducted
                ? [
                    {
                      text: `${insights.attendance.last30Days.rate}% last 30 days`,
                      tone: insights.attendance.last30Days.healthy ? "good" : "warn",
                    },
                  ]
                : [],
            },
            {
              icon: <FaBookOpen size={22} />,
              value: insights ? insights.courses.total : "—",
              label: "Courses Taught",
              tone: "sky",
              hint: !insights
                ? "Loading"
                : insights.courses.list.length
                  ? `Largest: ${insights.courses.list[0].name}`
                  : `Across ${insights.batches.classrooms} classroom${insights.batches.classrooms === 1 ? "" : "s"}`,
            },
            {
              icon: <FaTrophy size={22} />,
              value: insights ? insights.performance.score : "—",
              label: "Performance Score",
              tone: PERFORMANCE_TONE[insights?.performance?.level] || "berry",
              chips: insights
                ? [
                    {
                      text: insights.performance.band,
                      tone: PERFORMANCE_CHIP_TONE[insights.performance.level] ?? "warn",
                    },
                  ]
                : [],
              hint: insights
                ? `${insights.performance.avgStudentAttendance}% average attendance`
                : "Loading",
            },
            {
              icon: <FaUserTie size={22} />,
              value: insights ? insights.mockInterviews.assigned : "—",
              label: "Mock Interviews",
              goTab: 'mockinterviews',
              tone: "indigo",
              chips:
                insights && insights.mockInterviews.assigned > 0
                  ? [
                      { text: `${insights.mockInterviews.attended} attended`, tone: "quiet" },
                      ...(insights.mockInterviews.successRate === null
                        ? []
                        : [
                            {
                              text: `${insights.mockInterviews.successRate}% qualified`,
                              tone: insights.mockInterviews.successHealthy ? "good" : "warn",
                            },
                          ]),
                    ]
                  : [],
              hint: insights ? "None assigned to you" : "Loading",
            },
          ].map((card) => {
            /* A card that stands for a list goes to the tab holding it.
               Deliberately not Waypoint: these are this trainer's totals,
               and Waypoint addresses one record by id -- there is no
               "my 12 batches" frame, and the tabs on this page already
               list them better than a dialog would. Same rule as the
               admin dashboard's tiles.

               "Courses Taught" and "Performance Score" have no tab: one
               is a count with no course list on this page, the other is a
               derived figure rather than a collection. */
            /* Switching the tab is not enough on its own: the strip sits
               below the stat cards, so on most screens the tab changed
               off-screen and the card looked like it had done nothing.
               The scroll is what makes the change visible. rAF so it runs
               after the new panel has rendered, and the strip's own
               scroll-margin-top clears the fixed header (TrainerDashboard
               .css) rather than the offset being computed here. */
            const go = card.goTab
              ? () => {
                  setActiveTab(card.goTab);
                  requestAnimationFrame(() => {
                    tabsRef.current?.scrollIntoView({
                      behavior: prefersReducedMotion() ? "auto" : "smooth",
                      block: "start",
                    });
                  });
                }
              : null;
            const Tag = go ? "button" : "div";
            return (
              <Tag
                className={`sm-vital sm-vital--${card.tone || "sky"}${go ? " sm-vital--go" : ""}`}
                key={card.label}
                {...(go
                  ? { type: "button", onClick: go, title: `Show ${card.label.toLowerCase()}` }
                  : {})}
              >
                {/* Label first, figure second. A caption under a huge
                    number reads as a poster; above it, the card reads as
                    a row in a report. */}
                <div className="sm-vital-head">
                  <span className="sm-vital-label">{card.label}</span>
                  <span className="sm-vital-icon">{card.icon}</span>
                </div>

                <div className="sm-vital-value">{card.value}</div>

                {card.chips && card.chips.length > 0 ? (
                  <div className="sm-vital-meta">
                    {card.chips.map((chip, i) => (
                      <span
                        key={chip.text}
                        className={`sm-vital-stat${chip.tone ? ` sm-vital-stat--${chip.tone}` : ""}`}
                      >
                        {i > 0 && <i className="sm-vital-sep" aria-hidden="true" />}
                        {chip.text}
                      </span>
                    ))}
                  </div>
                ) : (
                  card.hint && <p className="sm-vital-meta">{card.hint}</p>
                )}
              </Tag>
            );
          })}
          {/* Live count of this trainer's own students who are online.
              Not for an admin viewing a trainer: the roster behind it is
              scoped server-side to the CALLER's batches, so an admin would
              see every student under this trainer's name -- the same
              reason MyFeedbackSummary is trainer-only below. */}
          {!isAdmin && <OnlineStudentsBox scope="trainer" variant="vital" />}
          {/* <Cell xl={4} lg={6}>
            <Card className="stats-card stats-card-orange-light h-100 border-0">
              <CardBody className="p-3 d-flex align-items-center">
                <div className="stats-icon me-3">
                  <FaMapMarkerAlt size={22} />
                </div>
                <div className="stats-content">
                  <h2 className="stats-number">
                    {stats?.totalClassrooms || 0}
                  </h2>
                  <p className="stats-label">Classrooms</p>
                </div>
              </CardBody>
            </Card>
          </Cell> */}
            </div>
          </Cell>
        </Grid>

        {/* How the teaching is landing, directly under the name box.
            Not behind the Feedback tab: a trainer should not have to go
            looking to find out they are in the risk band, and a verdict
            one click away is a verdict most people never read.

            The same verdict the admin dashboard reaches, from the same
            thresholds -- see Feedback/feedbackBands.js.

            Only on a trainer's OWN dashboard. /summary is scoped by the
            server to the CALLER, so an admin reading someone else's would
            be shown their own figures under that trainer's name. */}
        {!isAdmin && (
          <MyFeedbackSummary onSeeAll={() => setActiveTab("feedback")} />
        )}

        {/* Today's Schedule (Hidden as requested because it hits classrooms API)
        {todaySchedule.length > 0 && (
          <Grid space="lg">
            <Cell>
              <Card className="schedule-card border-0">
                <CardHeader className="schedule-header border-0 p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h3 className="mb-1 text-white fw-bold">
                        <FaClock className="me-3" />
                        Today's Schedule
                      </h3>
                      <p className="mb-0 text-white-50">
                        Your personalized timeline for today
                      </p>
                    </div>
                    <Badge
                      bg="light"
                      text="dark"
                      className="px-3 py-2 rounded-pill"
                    >
                      {todaySchedule.length} Sessions
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody className="p-4">
                  <div className="timeline-container">
                    {todaySchedule.map((schedule, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-marker">
                          <div className="timeline-dot"></div>
                        </div>
                        <Card className="timeline-card border-0">
                          <CardBody className="p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h5 className="fw-bold mb-2 text-primary">
                                  {schedule.batchName}
                                </h5>
                                <p className="text-muted mb-0">
                                  {schedule.courseName}
                                </p>
                              </div>
                              {getStatusBadge(schedule.status)}
                            </div>
                            <div className="schedule-details">
                              <div className="detail-item">
                                <FaMapMarkerAlt className="detail-icon" />
                                <span>{schedule.classroomNo}</span>
                              </div>
                              <div className="detail-item">
                                <FaClock className="detail-icon" />
                                <span>
                                  {formatTime(schedule.startTime)} -{" "}
                                  {formatTime(schedule.endTime)}
                                </span>
                              </div>
                            </div>
                            <div className="timeline-actions mt-3">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2"
                              >
                                <FaVideo className="me-1" /> Join Class
                              </Button>
                              <Button size="sm" variant="outline-secondary">
                                <FaEye className="me-1" /> Details
                              </Button>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Cell>
          </Grid>
        )} */}

        {/* Main Tabs — bare markup. react-bootstrap's Nav/Tab render
            .nav, .nav-link, .card, .tab-pane and .tab-content, all of which
            style.css paints, so the tab box could never look unstyled while
            those components were in place. This is plain elements driven by
            the activeTab state instead: no classes, no Bootstrap. */}
        <div className="td-tabs" ref={tabsRef}>
          <div className="td-tabs-strip" role="tablist">
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "overview" ? " is-on" : ""}`}
              aria-selected={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "batches" ? " is-on" : ""}`}
              aria-selected={activeTab === "batches"}
              onClick={() => setActiveTab("batches")}
            >
              My Batches
            </button>
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "students" ? " is-on" : ""}`}
              aria-selected={activeTab === "students"}
              onClick={() => setActiveTab("students")}
            >
              All Students
            </button>
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "attendance" ? " is-on" : ""}`}
              aria-selected={activeTab === "attendance"}
              onClick={() => setActiveTab("attendance")}
            >
              Attendance
            </button>
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "mockinterviews" ? " is-on" : ""}`}
              aria-selected={activeTab === "mockinterviews"}
              onClick={() => setActiveTab("mockinterviews")}
            >
              Mock Interviews
            </button>
            {/* What students said about the classes. Last in the strip
                because it is the one tab that reports on the trainer
                rather than on their work queue. */}
            <button
              type="button"
              role="tab"
              className={`td-tab${activeTab === "feedback" ? " is-on" : ""}`}
              aria-selected={activeTab === "feedback"}
              onClick={() => setActiveTab("feedback")}
            >
              Feedback
            </button>
          </div>

          <div className="td-tabs-body">
                    {/* Overview Tab */}
                    {activeTab === "overview" && (<>
                      <div>
                        <div>
                          <div>
                            <div>
                              <div className="td-search">
                                <FaSearch className="td-search-icon" />
                                <input
                                  className="td-search-input"
                                  type="text"
                                  placeholder="Search batches in overview..."
                                  value={tabSearchTerms.overview}
                                  onChange={(e) => handleSearch("overview", e.target.value)}
                                />
                                {tabSearchTerms.overview && (
                                  <button
                                    type="button"
                                    className="td-search-clear"
                                    aria-label="Clear search"
                                    onClick={() => handleSearch("overview", "")}
                                  >
                                    <FaTimes />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Today, and what needs attention. Derived from the
                            batch list already in memory — the old Today's
                            Schedule block was disabled because it needed a
                            second call to the classrooms API. */}
                        {/* Rendered unconditionally: both cards carry their own
                            empty state, and gating the Row on the three lists
                            being non-empty meant "Today's Classes" disappeared
                            entirely on a quiet day instead of saying so. */}
                        {(
                          <div className="td-duo">
                            <section className="td-panel">
                              <header className="td-panel-head">
                                <h5 className="td-panel-title">
                                  <FaClock />
                                  Today&apos;s Classes
                                  <span className="td-panel-count">
                                    {todayClasses.length}
                                  </span>
                                </h5>
                                {/* Needs Attention beside it says what its list
                                    is; this one said nothing, so the two headers
                                    did not read as a pair. */}
                                <p className="td-panel-sub">
                                  earliest first · today only · a row opens the batch
                                </p>
                              </header>

                              <div className="td-panel-body">
                                {todayClasses.length === 0 ? (
                                  <p className="td-empty">No batches scheduled to meet today.</p>
                                ) : (
                                  todayClasses.slice(0, 4).map((batch) => (
                                    <article
                                      className="td-item"
                                      key={batch.id || batch._id}
                                      onClick={() =>
                                        openRow(
                                          {
                                            title: batch.name,
                                            sub: batch.courseName,
                                            chips: [{ text: batch.mode || "Online" }],
                                            rows: [
                                              { label: "Runs", value: `${formatTimeOfDay(batch.startTime)} – ${formatTimeOfDay(batch.endTime)}` },
                                              { label: "Room", value: batch.classroomName },
                                              { label: "Students", value: batch.studentCount || 0 },
                                            ],
                                            actions: [
                                              { label: "Open batch", onClick: () => handleBatchClick(batch) },
                                              ...(batch.meetingLink
                                                ? [{ label: "Join class", onClick: () => window.open(batch.meetingLink, "_blank") }]
                                                : []),
                                            ],
                                          },
                                          () => handleBatchClick(batch),
                                        )
                                      }
                                    >
                                      <div className="td-item-main">
                                        <h6 className="td-item-title">{batch.name}</h6>
                                        <p className="td-item-sub">{batch.courseName}</p>

                                        <div className="td-chips">
                                          {getModeBadge(batch.mode)}
                                          <span className="td-chip td-chip--time">
                                            <FaClock />
                                            {formatTimeOfDay(batch.startTime)} – {formatTimeOfDay(batch.endTime)}
                                          </span>
                                          {/* `batch.classroom` is the classroom's id. The API
                                              resolves it to `classroomName` alongside — printing
                                              the raw field showed the ObjectId as the room. */}
                                          {batch.classroomName &&
                                            batch.classroomName !== "Not assigned" &&
                                            batch.classroomName !== "Unknown room" && (
                                              <span className="td-chip">Room {batch.classroomName}</span>
                                            )}
                                        </div>
                                      </div>

                                      <div className="td-item-side">
                                        <span className="td-item-metric">
                                          {batch.studentCount || 0} students
                                        </span>
                                        {batch.meetingLink && (
                                          <a
                                            className="td-action"
                                            href={batch.meetingLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <FaVideo />
                                            Join class
                                          </a>
                                        )}
                                      </div>
                                    </article>
                                  ))
                                )}

                                {todayClasses.length > 4 && (
                                  <p className="td-more">
                                    +{todayClasses.length - 4} more today
                                  </p>
                                )}
                              </div>
                            </section>

                            <section className="td-tbl td-tbl--attention">
                              <header className="td-tbl-head">
                                <div>
                                  <h5 className="td-tbl-title">Needs Attention</h5>
                                  <p className="td-tbl-sub">
                                    worst first · running batches only · act this week
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="td-tbl-action"
                                  onClick={() => {
                                    setActiveTab("batches");
                                    setBatchStatusFilter("active");
                                  }}
                                >
                                  Open batches →
                                </button>
                              </header>

                              {needsAttention.length === 0 ? (
                                <p className="td-tbl-empty">
                                  {!insights?.batches?.active
                                    ? "No running batches right now."
                                    : "All running batches are on track — attendance is holding and the syllabus is keeping pace."}
                                </p>
                              ) : (
                                <>
                                  <div className="td-tbl-cols">
                                    <span>Batch</span>
                                    <span>Flag</span>
                                    <span />
                                  </div>

                                  <div className="td-tbl-rows">
                                    {needsAttention.slice(0, 6).map(({ batch, stats: s, reasons }) => (
                                      <div className="td-tbl-row" key={`na-${batch.id || batch._id}`}>
                                        <div className="td-tbl-name" title={batch.name}>
                                          <b>{ellipsis(batch.name)}</b>
                                          <small>{batch.courseName}</small>
                                        </div>

                                        {/* The first reason is the worst one — the
                                            list is sorted by severity. The rest, and
                                            the figures behind them, are in the popup. */}
                                        <span className="td-tbl-pill td-tbl-pill--warn">
                                          {reasons[0].label}
                                          {reasons.length > 1 ? ` +${reasons.length - 1}` : ""}
                                        </span>

                                        <button
                                          type="button"
                                          className="td-tbl-btn"
                                          onClick={() =>
                                            openRow(
                                              {
                                                title: batch.name,
                                                sub: batch.courseName,
                                                chips: reasons.map((x) => ({ text: x.label, tone: "warn" })),
                                                rows: [
                                                  {
                                                    label: "Attendance",
                                                    value: s.attendanceRate === null ? "No sessions" : `${s.attendanceRate}%`,
                                                    tone: s.attendanceLow ? "bad" : undefined,
                                                  },
                                                  {
                                                    label: "Days left",
                                                    value: s.daysRemaining !== null && s.daysRemaining >= 0 ? `${s.daysRemaining}` : "Ended",
                                                  },
                                                  { label: "Syllabus", value: s.syllabusProgress === null ? "" : `${s.syllabusProgress}%` },
                                                  { label: "Timeline", value: s.elapsedProgress === null ? "" : `${s.elapsedProgress}%` },
                                                  { label: "Why flagged", value: reasons[0].detail },
                                                ],
                                                actions: [{ label: "Open batch", onClick: () => handleBatchClick(batch) }],
                                              },
                                              () => showFlaggedBatch(batch, s, reasons),
                                            )
                                          }
                                        >
                                          Details
                                        </button>
                                      </div>
                                    ))}
                                  </div>

                                  {/* The rule that put these rows here, stated once
                                      rather than repeated per row. */}
                                  <p className="td-tbl-note">
                                    <b>How a batch lands here:</b> attendance under 50%, the
                                    calendar running ahead of the syllabus, or the end date
                                    close with topics still left. Any one is enough.
                                  </p>
                                </>
                              )}
                            </section>
                          </div>
                        )}

                        {insights && (
                          /* Teaching Summary and Courses You Teach share a row.
                             The chart takes the wider half: its bars carry a
                             label axis and a percentage axis either side, so it
                             needs the space more than the table. */
                          <div className="td-duo td-duo--split">
                            <section className="td-bars">
                              <header className="td-bars-head">
                                <h5 className="td-bars-title">
                                  <FaChartLine />
                                  Teaching Summary
                                </h5>
                                <span className="td-bars-total">
                                  {insights.performance.band}
                                </span>
                              </header>

                              {/* Was six hand-drawn CSS bars on one track,
                                  four of them percentages and two of them
                                  counts squeezed onto the same scale. The
                                  chart puts the rates on a real 0-100 axis
                                  and the counts below it as figures -- same
                                  numbers, each on a scale it belongs to. */}
                              <div className="td-bars-body">
                                <TeachingSummaryChart insights={insights} />
                              </div>

                              {/* Each chip is a real button — the counts are the
                                  start of a question, so every one of them leads
                                  somewhere. */}
                              <div className="td-bars-chips">
                                <button
                                  type="button"
                                  className="td-bars-chip"
                                  onClick={() =>
                                    showStudentBand(
                                      "Attending well (85%+)",
                                      insights.students.strongList,
                                      "good",
                                    )
                                  }
                                >
                                  {insights.students.strong} attending well (85%+)
                                </button>
                                <button
                                  type="button"
                                  className="td-bars-chip"
                                  onClick={() =>
                                    showStudentBand(
                                      "Below 60% attendance",
                                      insights.students.atRiskList,
                                      "bad",
                                    )
                                  }
                                >
                                  {insights.students.atRisk} below 60%
                                </button>
                                <button
                                  type="button"
                                  className="td-bars-chip"
                                  onClick={() => setActiveTab("students")}
                                >
                                  {insights.students.inActiveBatches} in running batches
                                </button>
                                {insights.engagement.totalEvents > 0 && (
                                  <button
                                    type="button"
                                    className="td-bars-chip"
                                    onClick={() => navigate("/list-event-Form")}
                                  >
                                    {insights.engagement.totalEvents} event{insights.engagement.totalEvents === 1 ? "" : "s"}
                                    {insights.engagement.upcomingEvents > 0 &&
                                      ` · ${insights.engagement.upcomingEvents} upcoming`}
                                  </button>
                                )}
                                {insights.mockInterviews.assigned > 0 && (
                                  <button
                                    type="button"
                                    className="td-bars-chip"
                                    onClick={() => setActiveTab("mockinterviews")}
                                  >
                                    {insights.mockInterviews.assigned} mock interview{insights.mockInterviews.assigned === 1 ? "" : "s"}
                                  </button>
                                )}
                              </div>
                            </section>

                            <section className="td-tbl td-tbl--courses">
                              <header className="td-tbl-head">
                                <div>
                                  <h5 className="td-tbl-title">Courses You Teach</h5>
                                  <p className="td-tbl-sub">
                                    {insights.courses.total} course{insights.courses.total === 1 ? "" : "s"}
                                    {" · "}across your batches · newest load first
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="td-tbl-action"
                                  onClick={() => setActiveTab("batches")}
                                >
                                  Open batches →
                                </button>
                              </header>

                              {insights.courses.list.length === 0 ? (
                                <p className="td-tbl-empty">
                                  No courses linked to your batches yet.
                                </p>
                              ) : (
                                <>
                                  <div className="td-tbl-cols">
                                    <span>Course</span>
                                    <span>Batches</span>
                                    <span>Status</span>
                                    <span>Students</span>
                                  </div>

                                  <div className="td-tbl-rows">
                                    {(showAllCourses
                                      ? insights.courses.list
                                      : insights.courses.list.slice(0, 5)
                                    ).map((course) => (
                                      <div
                                        className="td-tbl-row td-tbl-row--tap"
                                        key={course.id}
                                        onClick={() =>
                                          openRow({
                                            title: course.name,
                                            sub: `${course.batches} batch${course.batches === 1 ? "" : "es"}`,
                                            chips: course.activeBatches > 0
                                              ? [{ text: `${course.activeBatches} running`, tone: "live" }]
                                              : [],
                                            rows: [
                                              { label: "Batches", value: course.batches },
                                              { label: "Running now", value: course.activeBatches },
                                              { label: "Students", value: course.students },
                                            ],
                                            actions: [
                                              { label: "Open course", onClick: () => openEntity("course", course.id) },
                                              { label: "See batches", onClick: () => setActiveTab("batches") },
                                            ],
                                          },
                                          /* On desktop this row did nothing at all:
                                             openRow with no second argument falls
                                             through. It names a course, so it opens
                                             the course. */
                                          () => openEntity("course", course.id),
                                        )
                                        }
                                      >
                                        <div className="td-tbl-name">
                                          <b>{course.name}</b>
                                          <small>
                                            {course.batches} batch{course.batches === 1 ? "" : "es"}
                                          </small>
                                        </div>
                                        <span className="td-tbl-num">{course.batches}</span>
                                        <span
                                          className={`td-tbl-pill${course.activeBatches > 0 ? " td-tbl-pill--live" : ""}`}
                                        >
                                          {course.activeBatches > 0
                                            ? `${course.activeBatches} running`
                                            : "Idle"}
                                        </span>
                                        <span className="td-tbl-num">{course.students}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}

                              {insights.courses.list.length > 5 && (
                                <button
                                  type="button"
                                  className="td-tbl-more"
                                  onClick={() => setShowAllCourses((open) => !open)}
                                  aria-expanded={showAllCourses}
                                >
                                  {showAllCourses
                                    ? "Show fewer courses"
                                    : `+${insights.courses.list.length - 5} more courses`}
                                </button>
                              )}
                            </section>
                          </div>
                        )}

                        <div>
                          {(() => {
                            // Split by state, narrowed by the search and
                            // totalled by the API; the panels only print.
                            const activeBatches = overview.active;
                            const upcomingBatches = overview.upcoming;
                            const completedBatches = overview.completed;
                            const activeTotals = overview.summary.active || {};
                            const upcomingTotals = overview.summary.upcoming || {};
                            const completedTotals = overview.summary.completed || {};

                            return (
                              <>
                                {(activeBatches.length > 0 || upcomingBatches.length > 0) && (
                                  <div className="td-duo">
                                    {activeBatches.length > 0 && (
                                      <section className="td-tbl td-tbl--batches">
                                        <header className="td-tbl-head">
                                          <div>
                                            <h5 className="td-tbl-title">Active Batches</h5>
                                            {/* Roll-up across every active batch, not just
                                                the three shown below -- totalled by the API. */}
                                            <p className="td-tbl-sub">
                                              {activeTotals.count || 0} running
                                              {" · "}
                                              {activeTotals.students || 0} student{activeTotals.students === 1 ? "" : "s"}
                                              {activeTotals.attendanceRate == null
                                                ? " · no sessions yet"
                                                : ` · ${activeTotals.attendanceRate}% attendance`}
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            className="td-tbl-action"
                                            onClick={() => {
                                              setActiveTab("batches");
                                              setBatchStatusFilter("active");
                                            }}
                                          >
                                            Open →
                                          </button>
                                        </header>

                                        <div className="td-tbl-cols">
                                          <span>Batch</span>
                                          <span>Students</span>
                                          <span>Attendance</span>
                                          <span />
                                        </div>

                                        <div className="td-tbl-rows">
                                          {activeBatches.slice(0, 3).map((batch, index) => {
                                            // The batch's own attendance, attached by the API.
                                            const att = batch.attendance || {};
                                            const totalStudents = batch.studentCount || 0;
                                            const avgAttendance = att.rate ?? null;
                                            return (
                                              <div className="td-tbl-row" key={index}>
                                                <div className="td-tbl-name" title={batch.name}>
                                                  <b>{ellipsis(batch.name)}</b>
                                                  <small>
                                                    {batch.courseName}
                                                    {batch.mode ? ` · ${batch.mode}` : ""}
                                                  </small>
                                                </div>

                                                <span className="td-tbl-num">{totalStudents}</span>

                                                <span
                                                  className={`td-tbl-num${att.low ? " td-tbl-num--alert" : ""}`}
                                                  title={
                                                    att.sessions
                                                      ? `${att.presentMarks} present of ${att.expectedMarks} expected across ${att.sessions} session${att.sessions === 1 ? "" : "s"}`
                                                      : "No attendance data"
                                                  }
                                                >
                                                  {avgAttendance === null ? "—" : `${avgAttendance}%`}
                                                </span>

                                                <button
                                                  type="button"
                                                  className="td-tbl-btn"
                                                  onClick={() =>
                                                    openRow(
                                                      {
                                                        title: batch.name,
                                                        sub: batch.courseName,
                                                        chips: [{ text: batch.mode || "Online" }, { text: "Active", tone: "live" }],
                                                        rows: [
                                                          { label: "Students", value: totalStudents },
                                                          { label: "Attendance", value: avgAttendance === null ? "No sessions" : `${avgAttendance}%`, tone: att.low ? "bad" : undefined },
                                                          { label: "Runs", value: `${formatDate(batch.startTime)} – ${formatDate(batch.endTime)}` },
                                                        ],
                                                        actions: [{ label: "Open batch", onClick: () => handleBatchClick(batch) }],
                                                      },
                                                      () => handleBatchClick(batch),
                                                    )
                                                  }
                                                >
                                                  Open
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>

                                        {activeBatches.length > 3 && (
                                          <button
                                            type="button"
                                            className="td-tbl-more"
                                            onClick={() => {
                                              setActiveTab("batches");
                                              setBatchStatusFilter("active");
                                            }}
                                          >
                                            +{activeBatches.length - 3} more active batches
                                          </button>
                                        )}
                                      </section>
                                    )}

                                    {upcomingBatches.length > 0 && (
                                      <section className="td-tbl td-tbl--upcoming">
                                        <header className="td-tbl-head">
                                          <div>
                                            <h5 className="td-tbl-title">Upcoming Batches</h5>
                                            <p className="td-tbl-sub">
                                              {upcomingTotals.count || 0} scheduled
                                              {" · "}
                                              {upcomingTotals.students || 0}{" "}
                                              student
                                              {upcomingTotals.students === 1 ? "" : "s"}{" "}
                                              enrolled · soonest first
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            className="td-tbl-action"
                                            onClick={() => {
                                              setActiveTab("batches");
                                              setBatchStatusFilter("upcoming");
                                            }}
                                          >
                                            Open →
                                          </button>
                                        </header>

                                        <div className="td-tbl-cols">
                                          <span>Batch</span>
                                          <span>Students</span>
                                          <span>Starts</span>
                                          <span />
                                        </div>

                                        <div className="td-tbl-rows">
                                          {upcomingBatches.slice(0, 3).map((batch, index) => {
                                            // Calendar days to the start, and whether that is
                                            // inside the week the row flags -- both from the API.
                                            const days = batch.startsInDays ?? 0;
                                            return (
                                              <div className="td-tbl-row" key={index}>
                                                <div className="td-tbl-name" title={batch.name}>
                                                  <b>{ellipsis(batch.name)}</b>
                                                  <small>
                                                    {batch.courseName}
                                                    {batch.mode ? ` · ${batch.mode}` : ""}
                                                  </small>
                                                </div>

                                                <span className="td-tbl-num">
                                                  {batch.studentCount || 0}
                                                </span>

                                                <span
                                                  className={`td-tbl-num${batch.startsSoon ? " td-tbl-num--alert" : ""}`}
                                                  title={`Starts ${formatDate(batch.startTime)}`}
                                                >
                                                  {days === 0 ? "Today" : `${days}d`}
                                                </span>

                                                <button
                                                  type="button"
                                                  className="td-tbl-btn"
                                                  onClick={() =>
                                                    openRow(
                                                      {
                                                        title: batch.name,
                                                        sub: batch.courseName,
                                                        chips: [{ text: batch.mode || "Online" }, { text: "Upcoming", tone: "soon" }],
                                                        rows: [
                                                          { label: "Students", value: batch.studentCount || 0 },
                                                          { label: "Starts in", value: days === 0 ? "Today" : `${days} days` },
                                                          { label: "Runs", value: `${formatDate(batch.startTime)} – ${formatDate(batch.endTime)}` },
                                                        ],
                                                        actions: [{ label: "Open batch", onClick: () => handleBatchClick(batch) }],
                                                      },
                                                      () => handleBatchClick(batch),
                                                    )
                                                  }
                                                >
                                                  Open
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>

                                        {upcomingBatches.length > 3 && (
                                          <button
                                            type="button"
                                            className="td-tbl-more"
                                            onClick={() => {
                                              setActiveTab("batches");
                                              setBatchStatusFilter("upcoming");
                                            }}
                                          >
                                            +{upcomingBatches.length - 3} more upcoming batches
                                          </button>
                                        )}
                                      </section>
                                    )}
                                  </div>
                                )}

                                {completedBatches.length > 0 && (
                                   <section className="td-tbl td-tbl--completed">
                                     <header className="td-tbl-head">
                                       <div>
                                         <h5 className="td-tbl-title">Completed Batches</h5>
                                         <p className="td-tbl-sub">
                                           {completedTotals.count || 0} finished · most recent first
                                         </p>
                                       </div>
                                       <button
                                         type="button"
                                         className="td-tbl-action"
                                         onClick={() => {
                                           setActiveTab("batches");
                                           setBatchStatusFilter("completed");
                                         }}
                                       >
                                         Open →
                                       </button>
                                     </header>

                                     <div className="td-tbl-cols">
                                       <span>Batch</span>
                                       <span>Students</span>
                                       <span>Ended</span>
                                       <span />
                                     </div>

                                     <div className="td-tbl-rows">
                                       {completedBatches.slice(0, 3).map((batch, index) => (
                                         <div className="td-tbl-row" key={index}>
                                           <div className="td-tbl-name" title={batch.name}>
                                             <b>{ellipsis(batch.name)}</b>
                                             <small>
                                               {batch.courseName}
                                               {batch.mode ? " · " + batch.mode : ""}
                                             </small>
                                           </div>

                                           <span className="td-tbl-num">
                                             {batch.studentCount || 0}
                                           </span>

                                           <span className="td-tbl-num">
                                             {formatDate(batch.endTime)}
                                           </span>

                                           <button
                                             type="button"
                                             className="td-tbl-btn"
                                             onClick={() =>
                                               openRow(
                                                 {
                                                   title: batch.name,
                                                   sub: batch.courseName,
                                                   chips: [{ text: batch.mode || "Online" }, { text: "Completed", tone: "done" }],
                                                   rows: [
                                                     { label: "Students", value: batch.studentCount || 0 },
                                                     { label: "Ended", value: formatDate(batch.endTime) },
                                                   ],
                                                   actions: [{ label: "Open batch", onClick: () => handleBatchClick(batch) }],
                                                 },
                                                 () => handleBatchClick(batch),
                                               )
                                             }
                                           >
                                             Open
                                           </button>
                                         </div>
                                       ))}
                                     </div>

                                     {completedBatches.length > 3 && (
                                       <button
                                         type="button"
                                         className="td-tbl-more"
                                         onClick={() => {
                                           setActiveTab("batches");
                                           setBatchStatusFilter("completed");
                                         }}
                                       >
                                         +{completedBatches.length - 3} more completed batches
                                       </button>
                                     )}
                                   </section>
                                )}

                                {tabSearchTerms.overview &&
                                  activeBatches.length === 0 &&
                                  upcomingBatches.length === 0 &&
                                  completedBatches.length === 0 && (
                                    <div className="td-state">
                                      <span className="td-state-icon">
                                        <FaSearch size={26} />
                                      </span>
                                      <h6 className="td-state-title">No batches found</h6>
                                      <p className="td-state-text">
                                        Nothing matches &ldquo;{tabSearchTerms.overview}&rdquo;.
                                      </p>
                                      <button
                                        type="button"
                                        className="td-card-btn"
                                        onClick={() => handleSearch("overview", "")}
                                      >
                                        Clear search
                                      </button>
                                    </div>
                                  )}

                                {!tabSearchTerms.overview && batches.length === 0 && (
                                  <div className="td-state">
                                    <span className="td-state-icon">
                                      <FaGraduationCap size={26} />
                                    </span>
                                    <h6 className="td-state-title">No batches yet</h6>
                                    <p className="td-state-text">
                                      No training batches have been assigned to you.
                                    </p>
                                  </div>
                                )}
                              </>
                            );
                          })()}

                        </div>
                      </div>
                    </>)}

                    {/* Batches Tab */}
                    {activeTab === "batches" && (<>
                      <div>
                         <div className="td-tabhead">
                           <div className="td-tabhead-row">

                             {/* Status segmented control — one button per
                                 state, each carrying its own count. */}
                             <div className="td-seg" role="group" aria-label="Batch status">
                              {(() => {
                                // Counted by the API over everything the filters
                                // matched, not over the rows loaded so far.
                                const counts = batchCounts || dashboardData?.batchCounts || {};
                                const activeCount = counts.active || 0;
                                const upcomingCount = counts.upcoming || 0;
                                const completedCount = counts.completed || 0;

                                return (
                                  <>
                                    <button
                                      type="button"
                                      className={`td-seg-btn td-seg-btn--active${batchStatusFilter === "active" ? " is-on" : ""}`}
                                      aria-pressed={batchStatusFilter === "active"}
                                      onClick={() => setBatchStatusFilter("active")}
                                    >
                                      <FaRocket size={13} />
                                      Active
                                      <span className="td-seg-count">{activeCount}</span>
                                    </button>

                                    <button
                                      type="button"
                                      className={`td-seg-btn td-seg-btn--upcoming${batchStatusFilter === "upcoming" ? " is-on" : ""}`}
                                      aria-pressed={batchStatusFilter === "upcoming"}
                                      onClick={() => setBatchStatusFilter("upcoming")}
                                    >
                                      <FaCalendarAlt size={13} />
                                      Upcoming
                                      <span className="td-seg-count">{upcomingCount}</span>
                                    </button>

                                    <button
                                      type="button"
                                      className={`td-seg-btn td-seg-btn--completed${batchStatusFilter === "completed" ? " is-on" : ""}`}
                                      aria-pressed={batchStatusFilter === "completed"}
                                      onClick={() => setBatchStatusFilter("completed")}
                                    >
                                      <FaTrophy size={13} />
                                      Completed
                                      <span className="td-seg-count">{completedCount}</span>
                                    </button>
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          <div className="td-searchbar">
                            <div className="td-search">
                              <FaSearch className="td-search-icon" />
                              <input
                                className="td-search-input"
                                type="text"
                                placeholder="Search batches..."
                                value={tabSearchTerms.batches}
                                onChange={(e) => handleSearch("batches", e.target.value)}
                              />
                              {tabSearchTerms.batches && (
                                <button
                                  type="button"
                                  className="td-search-clear"
                                  aria-label="Clear search"
                                  onClick={() => handleSearch("batches", "")}
                                >
                                  <FaTimes />
                                </button>
                              )}
                            </div>
                            <span className="td-searchbar-count">
                              Total: {tabPagination.batches.totalCount}
                            </span>
                          </div>
                        </div>

                        {/* ── Filters ────────────────────────────────────
                            Always open. Every control is visible on arrival, so
                            what the list can be narrowed by is apparent without
                            having to go looking for it. */}
                        <button
                          type="button"
                          className="td-filter-trigger"
                          onClick={() => setFilterSheet("batches")}
                        >
                          Add filters
                          {activeBatchFilterCount > 0 && (
                            <span className="td-filters-badge">{activeBatchFilterCount}</span>
                          )}
                        </button>

                        <div className={`td-filters td-sheet${filterSheet === "batches" ? " is-open" : ""}`}>
                          <div className="td-sheet-bar">
                            <span>Filters</span>
                            <button
                              type="button"
                              className="td-sheet-done"
                              onClick={() => setFilterSheet(null)}
                            >
                              Done
                            </button>
                          </div>
                          <div className="td-filters-head">
                            <div className="td-filters-headline">
                              <span className="td-filters-label">
                                Filters
                                {activeBatchFilterCount > 0 && (
                                  <span className="td-filters-badge">
                                    {activeBatchFilterCount} active
                                  </span>
                                )}
                              </span>

                              <div className="td-filters-tools">
                                <div className="td-field td-field--inline">
                                  <span className="td-field-label">Sort by</span>
                                  <select
                                    className="td-select"
                                    value={batchFilters.sort}
                                    onChange={(e) => updateBatchFilter("sort", e.target.value)}
                                  >
                                    <option value="start-desc">Newest start date</option>
                                    <option value="start-asc">Oldest start date</option>
                                    <option value="end-asc">Ending soonest</option>
                                    <option value="name-asc">Name (A–Z)</option>
                                    <option value="name-desc">Name (Z–A)</option>
                                    <option value="students-desc">Most students</option>
                                    <option value="students-asc">Fewest students</option>
                                    <option value="progress-desc">Most progressed</option>
                                  </select>
                                </div>

                                <button
                                  type="button"
                                  className="td-filters-clear"
                                  disabled={activeBatchFilterCount === 0}
                                  onClick={() => setBatchFilters(EMPTY_BATCH_FILTERS)}
                                >
                                  <FaTimes size={12} />
                                  Clear all
                                </button>
                              </div>
                            </div>

                            <div className="td-filters-body">
                              <div className="td-filters-grid">
                                <div className="td-field">
                                  <label className="td-field-label">Course</label>
                                  <select className="td-select" value={batchFilters.course} onChange={(e) => updateBatchFilter("course", e.target.value)}>
                                    <option value="">All courses</option>
                                    {batchFilterOptions.courses.map((c) => (
                                      <option key={c} value={c}>{c}</option>
                                    ))}
                                  </select>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Mode</label>
                                  <select className="td-select" value={batchFilters.mode} onChange={(e) => updateBatchFilter("mode", e.target.value)}>
                                    <option value="">All modes</option>
                                    {batchFilterOptions.modes.map((m) => (
                                      <option key={m} value={m}>{m}</option>
                                    ))}
                                  </select>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Classroom</label>
                                  <select className="td-select" value={batchFilters.classroom} onChange={(e) => updateBatchFilter("classroom", e.target.value)}>
                                    <option value="">All rooms</option>
                                    {/* value is the stored classroom id the filter
                                        matches; label is the room the API resolved. */}
                                    {batchFilterOptions.classrooms.map((c) => (
                                      <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                  </select>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Meets on</label>
                                  <select className="td-select" value={batchFilters.day} onChange={(e) => updateBatchFilter("day", e.target.value)}>
                                    <option value="">Any day</option>
                                    <option value="mon">Monday</option>
                                    <option value="tue">Tuesday</option>
                                    <option value="wed">Wednesday</option>
                                    <option value="thu">Thursday</option>
                                    <option value="fri">Friday</option>
                                    <option value="sat">Saturday</option>
                                    <option value="sun">Sunday</option>
                                  </select>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Has resource</label>
                                  <select className="td-select" value={batchFilters.resource} onChange={(e) => updateBatchFilter("resource", e.target.value)}>
                                    <option value="">Any</option>
                                    <option value="meeting">Meeting link</option>
                                    <option value="materials">Study materials</option>
                                    <option value="recordings">Recorded lectures</option>
                                    <option value="chat">Chat group</option>
                                    <option value="none">No resources linked</option>
                                  </select>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Starts between</label>
                                  <div className="td-range">
                                    <input
  className="td-input"
  type="date"
  value={batchFilters.dateFrom}
  onChange={(e) => updateBatchFilter("dateFrom", e.target.value)}
  aria-label="Starts on or after" />
                                    <span className="td-range-sep">to</span>
                                    <input
  className="td-input"
  type="date"
  value={batchFilters.dateTo}
  onChange={(e) => updateBatchFilter("dateTo", e.target.value)}
  aria-label="Starts on or before" />
                                  </div>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Class time between</label>
                                  <div className="td-range">
                                    <input
  className="td-input"
  type="time"
  value={batchFilters.timeFrom}
  onChange={(e) => updateBatchFilter("timeFrom", e.target.value)}
  aria-label="Class starts at or after" />
                                    <span className="td-range-sep">to</span>
                                    <input
  className="td-input"
  type="time"
  value={batchFilters.timeTo}
  onChange={(e) => updateBatchFilter("timeTo", e.target.value)}
  aria-label="Class starts at or before" />
                                  </div>
                                </div>

                                <div className="td-field">
                                  <label className="td-field-label">Class size</label>
                                  <div>
                                    <input className="td-input"
  type="number"
  min="0"
  placeholder="Min"
  value={batchFilters.minStudents}
  onChange={(e) => updateBatchFilter("minStudents", e.target.value)}
  aria-label="Minimum students" />
                                    <span className="td-range-sep">to</span>
                                    <input className="td-input"
  type="number"
  min="0"
  placeholder="Max"
  value={batchFilters.maxStudents}
  onChange={(e) => updateBatchFilter("maxStudents", e.target.value)}
  aria-label="Maximum students" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {(() => {
                          // Filtered, sorted and split by state by /mybatches;
                          // the tab only picks the state that is showing.
                          const filteredBatches = batchLists[batchStatusFilter] || [];

                          return filteredBatches.length > 0 ? (
                            <>
                              {/* Same row as Today's Classes in the overview tab:
                                  identity and chips on the left, the figure and
                                  the action pinned right. */}
                              <div className="td-itemlist">
                                {filteredBatches.map((batch, index) => {
                                  const isUpcoming = batch.status === "upcoming";
                                  const isCompleted = batch.status === "completed";
                                  const state = isCompleted
                                    ? "done"
                                    : isUpcoming
                                      ? "soon"
                                      : "live";

                                  return (
                                    <article
                                      className="td-item"
                                      key={index}
                                      onClick={() =>
                                        openRow(
                                          {
                                            title: batch.name,
                                            sub: batch.courseDetails?.name || batch.courseName,
                                            chips: [
                                              { text: isCompleted ? "Completed" : isUpcoming ? "Upcoming" : "Active", tone: state },
                                              { text: batch.mode || "Online" },
                                            ],
                                            rows: [
                                              { label: "Students", value: batch.studentCount || 0 },
                                              { label: "Classroom", value: batch.classroomName || batch.classroom },
                                              { label: "Runs", value: `${formatDate(batch.startTime || batch.startDate)} – ${formatDate(batch.endTime || batch.endDate)}` },
                                            ],
                                            actions: [
                                              { label: "Open batch", onClick: () => handleBatchClick(batch) },
                                              ...(batch.meetingLink && !isCompleted
                                                ? [{ label: "Join", onClick: () => window.open(batch.meetingLink, "_blank") }]
                                                : []),
                                            ],
                                          },
                                          () => handleBatchClick(batch),
                                        )
                                      }
                                    >
                                      <div className="td-item-main">
                                        <h6 className="td-item-title" title={batch.name}>
                                          {ellipsis(batch.name, 24)}
                                        </h6>
                                        <p className="td-item-sub">
                                          {batch.courseDetails?.name || batch.courseName}
                                        </p>

                                        <div className="td-chips">
                                          <span className={`td-chip td-chip--${state}`}>
                                            {isCompleted ? "Completed" : isUpcoming ? "Upcoming" : "Active"}
                                          </span>
                                          {getModeBadge(batch.mode || "Online")}
                                          <span className="td-chip td-chip--time">
                                            <FaCalendarAlt />
                                            {formatDate(batch.startTime || batch.startDate)} –{" "}
                                            {formatDate(batch.endTime || batch.endDate)}
                                          </span>
                                          {(batch.classroomName || batch.classroom) &&
                                            batch.classroomName !== "Not assigned" && (
                                              <span className="td-chip">
                                                Room {batch.classroomName || batch.classroom}
                                              </span>
                                            )}
                                        </div>
                                      </div>

                                      <div className="td-item-side">
                                        <span className="td-item-metric">
                                          {batch.studentCount || 0} student
                                          {batch.studentCount === 1 ? "" : "s"}
                                        </span>
                                        {batch.meetingLink && !isCompleted && (
                                          <button
                                            type="button"
                                            className="td-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(batch.meetingLink, "_blank");
                                            }}
                                          >
                                            <FaVideo />
                                            Join class
                                          </button>
                                        )}
                                      </div>
                                    </article>
                                  );
                                })}
                              </div>

                              {/* Where the reader is, and the sentinel the observer watches. */}
                              <div className="att-picklist-foot" ref={batchesEndRef}>
                                {loadingMore ? (
                                  <>
                                    <span className="pg-spinner att-spinner" aria-hidden="true" />
                                    <span>Loading more batches...</span>
                                  </>
                                ) : activeTabData.batches.length < tabPagination.batches.totalCount ? (
                                  <span>
                                    {activeTabData.batches.length} of {tabPagination.batches.totalCount} batches
                                  </span>
                                ) : (
                                  tabPagination.batches.totalCount > 0 && (
                                    <span>
                                      All {tabPagination.batches.totalCount} batch
                                      {tabPagination.batches.totalCount === 1 ? "" : "es"} loaded
                                    </span>
                                  )
                                )}
                              </div>
                            </>
                          ) : isTabLoading ? (
                            <div className="td-state">
                              <span className="td-state-spinner" role="status" aria-label="Loading" />
                              <p className="td-state-text">Fetching your latest training batches…</p>
                            </div>
                          ) : (
                            <div className="td-state">
                              <span className="td-state-icon">
                                <FaBookOpen size={30} />
                              </span>
                              <h5 className="td-state-title">
                                No {batchStatusFilter === "active" ? "active" : "completed"} batches found
                              </h5>
                              <p className="td-state-text">
                                Nothing matches your current filters or search.
                              </p>
                              {tabSearchTerms.batches && (
                                <button
                                  type="button"
                                  className="td-card-btn"
                                  onClick={() => handleSearch("batches", "")}
                                >
                                  <FaTimes /> Clear search
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </>)}

                    {/* Mock Interviews Tab */}
                    {/* Attendance Tab — who actually turned up, across every
                        batch the trainer owns. Filtering, sorting and paging all
                        happen on the API; this pane only renders what it is given. */}
                    {activeTab === "attendance" && (<>
                      <div>

                        <div className="td-tabhead">
                          <div className="td-tabhead-row">
                            <div>
                              <p className="td-tabhead-sub">
                                One row per student per batch. A session counts as attended
                                when the student is marked present on it.
                              </p>
                            </div>
                            <div className="td-tabhead-actions">
                              <button
                                type="button"
                                className="td-bar-reset"
                                onClick={() => setExportOpen(true)}
                                disabled={!attendanceRows.length}
                                aria-haspopup="dialog"
                                aria-expanded={exportOpen}
                              >
                                <FaFileCsv /> Export CSV
                              </button>
                              {attendanceFilterCount > 0 && (
                                <button
                                  type="button"
                                  className="td-bar-reset"
                                  onClick={() => setAttendanceFilters(EMPTY_ATTENDANCE_FILTERS)}
                                >
                                  <FaTimes size={11} />
                                  Clear {attendanceFilterCount} filter{attendanceFilterCount === 1 ? "" : "s"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Summary tiles — recomputed for the filtered set, so they
                            always describe what the table below is showing. */}
                        {attendanceSummary && (
                          <div className="td-tiles">
                            <div className="td-tile">
                              <b className="td-tile-num">{attendanceSummary.students}</b>
                              <span className="td-tile-label">Students</span>
                            </div>
                            <div className="td-tile">
                              <b className="td-tile-num">{attendanceSummary.batches}</b>
                              <span className="td-tile-label">Batches</span>
                            </div>
                            <div className="td-tile">
                              <b className="td-tile-num">{attendanceSummary.sessions}</b>
                              <span className="td-tile-label">Sessions held</span>
                            </div>
                            <div className="td-tile">
                              <b className="td-tile-num">
                                {attendanceSummary.averageRate === null ? "—" : `${attendanceSummary.averageRate}%`}
                              </b>
                              <span className="td-tile-label">Average attendance</span>
                            </div>
                            {/* The last two are filters, not readouts — clicking one
                                narrows the table to that band and clicking again
                                clears it. */}
                            <button
                              type="button"
                              className={`td-tile td-tile--btn td-tile--bad${attendanceFilters.band === "critical" ? " is-on" : ""}`}
                              aria-pressed={attendanceFilters.band === "critical"}
                              onClick={() =>
                                setAttendanceFilter("band", attendanceFilters.band === "critical" ? "" : "critical")
                              }
                            >
                              <b className="td-tile-num">{attendanceSummary.critical}</b>
                              <span className="td-tile-label">Below 50%</span>
                            </button>
                            <button
                              type="button"
                              className={`td-tile td-tile--btn td-tile--good${attendanceFilters.band === "strong" ? " is-on" : ""}`}
                              aria-pressed={attendanceFilters.band === "strong"}
                              onClick={() =>
                                setAttendanceFilter("band", attendanceFilters.band === "strong" ? "" : "strong")
                              }
                            >
                              <b className="td-tile-num">{attendanceSummary.strong}</b>
                              <span className="td-tile-label">85% and above</span>
                            </button>
                          </div>
                        )}

                        {/* Filters — one bar, same as the other tabs. */}
                        <button
                          type="button"
                          className="td-filter-trigger"
                          onClick={() => setFilterSheet("attendance")}
                        >
                          Add filters
                          {attendanceFilterCount > 0 && (
                            <span className="td-filters-badge">{attendanceFilterCount}</span>
                          )}
                        </button>

                        <div className={`td-bar td-bar--wrap td-sheet${filterSheet === "attendance" ? " is-open" : ""}`}>
                          <div className="td-sheet-bar">
                            <span>Filters</span>
                            <button
                              type="button"
                              className="td-sheet-done"
                              onClick={() => setFilterSheet(null)}
                            >
                              Done
                            </button>
                          </div>
                          <div className="td-bar-fields">
                            <div className="td-field">
                              <label className="td-field-label">Search student</label>
                              <input className="td-input"
  type="text"
  placeholder="Name, email or phone"
  value={attendanceFilters.search}
  onChange={(e) => setAttendanceFilter("search", e.target.value)} />
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Batch</label>
                              <select className="td-select" value={attendanceFilters.batchId} onChange={(e) => setAttendanceFilter("batchId", e.target.value)}>
                                <option value="">All batches</option>
                                {attendanceOptions.batches.map((b) => (
                                  <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Course</label>
                              <select className="td-select" value={attendanceFilters.courseName} onChange={(e) => setAttendanceFilter("courseName", e.target.value)}>
                                <option value="">All courses</option>
                                {attendanceOptions.courses.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Batch status</label>
                              <select className="td-select" value={attendanceFilters.batchStatus} onChange={(e) => setAttendanceFilter("batchStatus", e.target.value)}>
                                <option value="">Any</option>
                                <option value="active">Running</option>
                                <option value="upcoming">Upcoming</option>
                                <option value="completed">Completed</option>
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Mode</label>
                              <select className="td-select" value={attendanceFilters.mode} onChange={(e) => setAttendanceFilter("mode", e.target.value)}>
                                <option value="">Any mode</option>
                                {attendanceOptions.modes.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Attendance band</label>
                              <select className="td-select" value={attendanceFilters.band} onChange={(e) => setAttendanceFilter("band", e.target.value)}>
                                <option value="">Any band</option>
                                <option value="critical">Critical — below 50%</option>
                                <option value="weak">Weak — 50 to 74%</option>
                                <option value="ok">OK — 75 to 84%</option>
                                <option value="strong">Strong — 85%+</option>
                                <option value="none">No sessions held yet</option>
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Sessions between</label>
                              <div className="td-range">
                                <input
  className="td-input"
  type="date"
  value={attendanceFilters.dateFrom}
  onChange={(e) => setAttendanceFilter("dateFrom", e.target.value)}
  aria-label="Sessions on or after" />
                                <span className="td-range-sep">to</span>
                                <input
  className="td-input"
  type="date"
  value={attendanceFilters.dateTo}
  onChange={(e) => setAttendanceFilter("dateTo", e.target.value)}
  aria-label="Sessions on or before" />
                              </div>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Attendance % between</label>
                              <div>
                                <input className="td-input"
  type="number"
  min="0"
  max="100"
  placeholder="min"
  value={attendanceFilters.minRate}
  onChange={(e) => setAttendanceFilter("minRate", e.target.value)}
  aria-label="Minimum attendance percent" />
                                <span className="td-range-sep">to</span>
                                <input className="td-input"
  type="number"
  min="0"
  max="100"
  placeholder="max"
  value={attendanceFilters.maxRate}
  onChange={(e) => setAttendanceFilter("maxRate", e.target.value)}
  aria-label="Maximum attendance percent" />
                              </div>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Show only</label>
                              <select className="td-select" value={attendanceFilters.tail} onChange={(e) => setAttendanceFilter("tail", e.target.value)}>
                                <option value="">Everyone</option>
                                <option value="never">Never attended</option>
                                <option value="perfect">Perfect attendance</option>
                              </select>
                            </div>

                            <div className="td-field">
                              <label className="td-field-label">Sort by</label>
                              <select className="td-select" value={attendanceFilters.sort} onChange={(e) => setAttendanceFilter("sort", e.target.value)}>
                                <option value="rate-asc">Attendance — lowest first</option>
                                <option value="rate-desc">Attendance — highest first</option>
                                <option value="absent-desc">Most absences</option>
                                <option value="attended-desc">Most attended</option>
                                <option value="name-asc">Name A–Z</option>
                                <option value="name-desc">Name Z–A</option>
                                <option value="batch-asc">Batch name</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Table */}
                        {attendanceLoading ? (
                          <div className="td-state">
                            <span className="td-state-spinner" role="status" aria-label="Loading" />
                            <p className="td-state-text">Working out who attended…</p>
                          </div>
                        ) : attendanceRows.length === 0 ? (
                          <div className="td-state">
                            <span className="td-state-icon">
                              <FaClipboardCheck size={28} />
                            </span>
                            <h5 className="td-state-title">No attendance to show</h5>
                            <p className="td-state-text">
                              {attendanceFilterCount > 0
                                ? "No student matches these filters. Try widening the date range or clearing a filter."
                                : "No sessions have been marked for your batches yet."}
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="td-list td-list--attendance">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Student</th>
                                    <th>Batch</th>
                                    <th>Course</th>
                                    <th>Sessions</th>
                                    <th>Present</th>
                                    <th>Absent</th>
                                    <th>Attendance</th>
                                    <th>Last present</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {attendanceRows.map((r) => (
                                    <tr
                                      key={`${r.studentId}-${r.batchId}`}
                                      onClick={() =>
                                        openRow({
                                          title: r.name,
                                          sub: r.email || r.phone,
                                          chips: [{ text: r.batchName }, ...(r.mode ? [{ text: r.mode }] : [])],
                                          rows: [
                                            { label: "Course", value: r.courseName },
                                            { label: "Sessions", value: r.sessions },
                                            { label: "Present", value: r.attended },
                                            { label: "Absent", value: r.absent },
                                            {
                                              label: "Attendance",
                                              value: r.rate === null ? "No sessions" : `${r.rate}%`,
                                              tone: r.rate !== null && r.rate < 50 ? "bad" : r.rate >= 85 ? "good" : undefined,
                                            },
                                            { label: "Last present", value: r.lastPresentAt ? formatDate(r.lastPresentAt) : "" },
                                            { label: "Phone", value: r.phone },
                                          ],
                                          actions: r.studentId
                                            ? [{ label: "Open student", onClick: () => openEntity("student", r.studentId) }]
                                            : [],
                                        },
                                        /* Another row that did nothing on desktop.
                                           Every figure in it is about one student,
                                           so clicking it opens that student. */
                                        r.studentId ? () => openEntity("student", r.studentId) : undefined,
                                      )
                                      }
                                      className={
                                        r.rate === null
                                          ? undefined
                                          : r.rate < 50
                                            ? "is-low"
                                            : r.rate >= 85
                                              ? "is-high"
                                              : undefined
                                      }
                                    >
                                      <td>
                                        <div className="td-person">
                                          <span
                                            className={`td-person-avatar td-person-avatar--${avatarTone(r.name || r.email)}`}
                                            aria-hidden="true"
                                          >
                                            {initialsOf(r.name, r.email)}
                                          </span>
                                          <span className="td-person-id">
                                            <b className="td-person-name">{r.name || "—"}</b>
                                            {(r.email || r.phone) && (
                                              <small className="td-person-mail">
                                                {r.email}
                                                {r.email && r.phone ? " · " : ""}
                                                {r.phone}
                                              </small>
                                            )}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="td-person-name">{r.batchName}</span>
                                        {r.mode && <small className="td-person-mail">{r.mode}</small>}
                                      </td>
                                      <td>{r.courseName}</td>
                                      <td className="td-num">{r.sessions}</td>
                                      <td className="td-num">{r.attended}</td>
                                      <td className="td-num">{r.absent}</td>
                                      <td className="td-num">
                                        {r.rate === null ? (
                                          <span className="td-person-none">No sessions</span>
                                        ) : (
                                          <span className={`td-rate-num${r.rate < 50 ? " td-rate-num--low" : r.rate >= 85 ? " td-rate-num--high" : ""}`}>{r.rate}%</span>
                                        )}
                                      </td>
                                      <td>
                                        {r.lastPresentAt ? formatDate(r.lastPresentAt) : (
                                          <span className="td-person-none">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div className="att-picklist-foot" ref={attendanceEndRef}>
                              {loadingMore ? (
                                <>
                                  <span className="pg-spinner att-spinner" aria-hidden="true" />
                                  <span>Loading more students...</span>
                                </>
                              ) : attendanceRows.length < attendancePagination.total ? (
                                <span>
                                  {attendanceRows.length} of {attendancePagination.total} students
                                </span>
                              ) : (
                                attendancePagination.total > 0 && (
                                  <span>
                                    All {attendancePagination.total} student
                                    {attendancePagination.total === 1 ? "" : "s"} loaded
                                  </span>
                                )
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </>)}

                    {activeTab === "mockinterviews" && (<>
                      <div>
                        <div className="td-tabhead">
                          <div className="td-tabhead-row">
                            <div className="td-seg" role="group" aria-label="When">
                              {[
                                { key: "all", label: "All" },
                                { key: "upcoming", label: "Upcoming" },
                                { key: "past", label: "Past" },
                              ].map((opt) => (
                                <button
                                  type="button"
                                  key={opt.key}
                                  className={`td-seg-btn td-seg-btn--${opt.key}${mockWhen === opt.key ? " is-on" : ""}`}
                                  aria-pressed={mockWhen === opt.key}
                                  onClick={() => setMockWhen(opt.key)}
                                >
                                  {opt.label}
                                  {/* Counted by the API over the whole match. */}
                                  <span className="td-seg-count">{mockCounts[opt.key] ?? 0}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                        </div>

                        {/* One bar: search plus both ranges, which the API applies. */}
                        <div className="td-bar td-bar--wrap">
                          <div className="td-bar-fields">
                            <div className="td-search td-search--sm">
                              <FaSearch className="td-search-icon" />
                              <input
                                className="td-search-input"
                                type="text"
                                placeholder="Search mock interviews…"
                                value={mockSearch}
                                onChange={(e) => setMockSearch(e.target.value)}
                              />
                              {mockSearch && (
                                <button
                                  type="button"
                                  className="td-search-clear"
                                  aria-label="Clear search"
                                  onClick={() => setMockSearch("")}
                                >
                                  <FaTimes />
                                </button>
                              )}
                            </div>
                            <div className="td-field">
                              <label className="td-field-label">Scheduled between</label>
                              <div className="td-range">
                                <input
  className="td-input"
  type="date"
  value={mockDateFrom}
  onChange={(e) => setMockDateFrom(e.target.value)}
  aria-label="Scheduled on or after" />
                                <span className="td-range-sep">to</span>
                                <input
  className="td-input"
  type="date"
  value={mockDateTo}
  onChange={(e) => setMockDateTo(e.target.value)}
  aria-label="Scheduled on or before" />
                              </div>
                            </div>
                            <div className="td-field">
                              <label className="td-field-label">Start time between</label>
                              <div className="td-range">
                                <input
  className="td-input"
  type="time"
  value={mockTimeFrom}
  onChange={(e) => setMockTimeFrom(e.target.value)}
  aria-label="Starts at or after" />
                                <span className="td-range-sep">to</span>
                                <input
  className="td-input"
  type="time"
  value={mockTimeTo}
  onChange={(e) => setMockTimeTo(e.target.value)}
  aria-label="Starts at or before" />
                              </div>
                            </div>
                            {(mockDateFrom || mockDateTo || mockTimeFrom || mockTimeTo) && (
                              <button type="button" onClick={() => {
                                  setMockDateFrom("");
                                  setMockDateTo("");
                                  setMockTimeFrom("");
                                  setMockTimeTo("");
                                }}>
                                <FaTimes size={12} />
                                Clear
                              </button>
                            )}
                          </div>
                        </div>

                        {mockLoading ? (
                          <div className="td-state">
                            <span className="td-state-spinner" role="status" aria-label="Loading" />
                            <p className="td-state-text">Loading your mock interview schedule…</p>
                          </div>
                        ) : (() => {
                          // Narrowed by When and ordered newest first by the API.
                          const rows = mockList;

                          if (rows.length === 0) {
                            return (
                              <div className="td-state">
                                <span className="td-state-icon">
                                  <FaUserTie size={28} />
                                </span>
                                <h5 className="td-state-title">No mock interviews found</h5>
                                <p className="td-state-text">
                                  {mockSearch
                                    ? "Nothing matches that search."
                                    : "No mock interviews have been scheduled for you yet."}
                                </p>
                              </div>
                            );
                          }

                          return (
                            <>
                            <div className="td-list td-list--mock">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Interview</th>
                                    <th>When</th>
                                    <th>Status</th>
                                    <th>Attended</th>
                                    <th>Avg score</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((ev) => {
                                    const stat = mockStats[ev.id];
                                    const isUpcoming = ev.when === "upcoming";
                                    const batchNames = Array.isArray(ev.batchId)
                                      ? ev.batchId.map((b) => b?.name).filter(Boolean)
                                      : [];
                                    const trainerNames = Array.isArray(ev.trainerId)
                                      ? ev.trainerId.map((t) => t?.trainerName).filter(Boolean)
                                      : [];

                                    return (
                                      /* The row, not a button at the end of it.
                                         Every other list here opens on the row;
                                         a "Details" column was one more thing to
                                         aim at for the same result. */
                                      <tr
                                        key={ev.id}
                                        onClick={() =>
                                          openRow(
                                            {
                                              title: ev.name,
                                              sub: `${formatDate(ev.date)} · ${formatTimeUTC(ev.time)}`,
                                              chips: [{ text: isUpcoming ? "Upcoming" : "Completed", tone: isUpcoming ? "soon" : "done" }],
                                              rows: [
                                                { label: "Attended", value: stat ? `${stat.attended}${stat.expected > 0 ? ` of ${stat.expected}` : ""}` : "" },
                                                { label: "Average score", value: stat && stat.performanceRate !== null ? `${stat.performanceRate}%` : "" },
                                                { label: "Pass rate", value: stat && stat.evaluated > 0 ? `${stat.passRate}%` : "" },
                                                { label: "Batches", value: batchNames.join(", ") },
                                                { label: "Trainers", value: trainerNames.join(", ") },
                                                { label: "Venue", value: ev.offlineAddress },
                                                { label: "Classroom", value: ev.classroomNumber },
                                                { label: "Organiser", value: ev.organizerName },
                                                { label: "Priority", value: ev.priority },
                                              ],
                                              actions: [
                                                { label: "Open interview", onClick: () => openEntity("event", ev.id) },
                                                { label: "Evaluate students", onClick: () => navigate("/mock-interview") },
                                              ],
                                            },
                                            /* Was a SweetAlert built from an HTML
                                               string: a dead end that named the
                                               batches and trainers on this event
                                               and reached neither, and painted
                                               itself from the old --ink/--line
                                               layer rather than the palette. The
                                               event frame carries both as tabs. */
                                            () => openEntity("event", ev.id),
                                          )
                                        }
                                      >
                                        <td>
                                          <span className="td-person-name" title={ev.name}>
                                            {ellipsis(ev.name, 28)}
                                          </span>
                                          <small className="td-person-mail">
                                            {batchNames.length
                                              ? ellipsis(batchNames.join(", "), 32)
                                              : "No batch linked"}
                                          </small>
                                        </td>
                                        <td className="td-nowrap">
                                          {formatDate(ev.date)}
                                          <small className="td-person-mail">{formatTimeUTC(ev.time)}</small>
                                        </td>
                                        <td>
                                          <span className={`td-chip td-chip--${isUpcoming ? "soon" : "done"}`}>
                                            {isUpcoming ? "Upcoming" : "Completed"}
                                          </span>
                                        </td>
                                        <td className="td-num">
                                          {stat ? stat.attended : "\u2014"}
                                          {stat && stat.expected > 0 && (
                                            <span className="td-tile-of"> / {stat.expected}</span>
                                          )}
                                        </td>
                                        <td className="td-num">
                                          {stat && stat.performanceRate !== null ? `${stat.performanceRate}%` : "\u2014"}
                                          {/* Was only ever readable inside the popup
                                              this row used to open. It qualifies the
                                              score above it, so it reads here. */}
                                          {stat && stat.evaluated > 0 && (
                                            <small className="td-person-mail">{stat.passRate}% passed</small>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div className="att-picklist-foot" ref={mockEndRef}>
                              {loadingMore ? (
                                <>
                                  <span className="pg-spinner att-spinner" aria-hidden="true" />
                                  <span>Loading more interviews...</span>
                                </>
                              ) : mockList.length < mockTotal ? (
                                <span>
                                  {mockList.length} of {mockTotal} interviews
                                </span>
                              ) : (
                                mockTotal > 0 && (
                                  <span>
                                    All {mockTotal} interview{mockTotal === 1 ? "" : "s"} loaded
                                  </span>
                                )
                              )}
                            </div>
                            </>
                          );
                        })()}
                      </div>
                    </>)}

                    {/* Students Tab */}
                    {activeTab === "students" && (<>
                      <div>
                        {/* One horizontal bar: search, batch, enrolment range and
                            the resets. The date range is applied by the API, not
                            by trimming the page in the browser. */}
                        <div className="td-bar">
                          <div className="td-search td-search--sm">
                            <FaSearch className="td-search-icon" />
                            <input
                              className="td-search-input"
                              type="text"
                              placeholder="Search students…"
                              value={tabSearchTerms.students}
                              onChange={(e) => handleSearch("students", e.target.value)}
                            />
                            {tabSearchTerms.students && (
                              <button
                                type="button"
                                className="td-search-clear"
                                aria-label="Clear search"
                                onClick={() => handleSearch("students", "")}
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>

                          <select
                            className="td-select td-select--sm"
                            aria-label="Filter by batch"
                            value={batchFilter}
                            onChange={(e) => {
                              setBatchFilter(e.target.value);
                              handlePageChange("students", 1);
                            }}
                          >
                            <option value="">All batches</option>
                            {batches.map((batch, idx) => (
                              <option key={idx} value={batch.id}>
                                {batch.name}
                              </option>
                            ))}
                          </select>

                          <div className="td-bar-range">
                            <span className="td-bar-range-label">Enrolled</span>
                            <input
                              className="td-input td-input--sm"
                              type="date"
                              value={studentDateFrom}
                              onChange={(e) => setStudentDateFrom(e.target.value)}
                              aria-label="Enrolled on or after"
                            />
                            <span className="td-range-sep">to</span>
                            <input
                              className="td-input td-input--sm"
                              type="date"
                              value={studentDateTo}
                              onChange={(e) => setStudentDateTo(e.target.value)}
                              aria-label="Enrolled on or before"
                            />
                          </div>

                          {(batchFilter || studentDateFrom || studentDateTo || tabSearchTerms.students) && (
                            <button
                              type="button"
                              className="td-bar-reset"
                              onClick={() => {
                                setBatchFilter("");
                                setStudentDateFrom("");
                                setStudentDateTo("");
                                handleSearch("students", "");
                                handlePageChange("students", 1);
                              }}
                            >
                              <FaTimes size={11} />
                              Reset
                            </button>
                          )}
                        </div>

                        <div className="td-list td-list--students">
                        <NewCustomDataTable
  headers={studentColumns}
  data={activeTabData.students}
  totalRecords={tabPagination.students.totalCount}
  currentPage={tabPagination.students.page}
  recordsPerPage={itemsPerPage}
  onPageChange={(page) =>
                            handlePageChange("students", page)
                          }
  loading={isTabLoading}
  loadingMore={loadingMore}
  serverSide={true}
  showToolbar={false}
  showPagination={false}
  showFooter={false}
  /* This table had no row action at all: a trainer could see who is on
     their batches and reach none of them. Waypoint opens the student
     in place -- and from there their batches, and from a batch its
     roster -- without this page having to know what any of that looks
     like.

     Every cell wraps its text in a span, so the default ['TR','TD']
     would mean clicking a name did nothing and only the padding around
     it worked. */
  onRowClick={(row) => {
    const sid = row?.studentId || row?.id;
    if (sid) openEntity('student', sid);
  }}
  validRowClickTagNames="*"
  rowProps={(row) => ({
    tabIndex: 0,
    role: 'button',
    'aria-label': `Open ${row?.studentName || row?.name || 'this student'}`,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const sid = row?.studentId || row?.id;
        if (sid) openEntity('student', sid);
      }
    },
  })} />
                        </div>
                        {/* Where the reader is, and the sentinel the observer watches. */}
                        <div className="att-picklist-foot" ref={studentsEndRef}>
                          {loadingMore ? (
                            <>
                              <span className="pg-spinner att-spinner" aria-hidden="true" />
                              <span>Loading more students...</span>
                            </>
                          ) : activeTabData.students.length < tabPagination.students.totalCount ? (
                            <span>
                              {activeTabData.students.length} of {tabPagination.students.totalCount} students
                            </span>
                          ) : (
                            tabPagination.students.totalCount > 0 && (
                              <span>
                                All {tabPagination.students.totalCount} student
                                {tabPagination.students.totalCount === 1 ? "" : "s"} loaded
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    </>)}

                    {/* What students said about this trainer's classes.
                        FeedbackList is the same component the admin page
                        renders -- the server scopes it to this trainer's
                        own sessions and strips the student identity, so
                        there is nothing to gate here. */}
                    {activeTab === "feedback" && (
                      <FeedbackList
                        variant="panel"
                        /* Whose dashboard this is. A trainer reading their
                           own is scoped by the server anyway and this is
                           their own id, so it changes nothing; an ADMIN
                           reading someone else's is scoped to everything,
                           and without this the tab showed the whole
                           institution's feedback under that trainer's
                           name. */
                        trainerId={trainerId || undefined}
                        title={isAdmin ? "What their students said" : "What your students said"}
                        sub="Per session, per topic, and how they rated the teaching. Names are not shown."
                      />
                    )}
          </div>
        </div>

        <RowSheet sheet={rowSheet} onClose={() => setRowSheet(null)} />

        {/* Who is behind one of the Teaching Summary's counts. The house
            popup, so it follows the palette; every name opens the student
            through Waypoint, which the SweetAlert this replaced could not
            do -- its rows were an HTML string. */}
        {studentBand && (
          <div
            className="pg-modal"
            role="presentation"
            onClick={() => setStudentBand(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              role="dialog"
              aria-modal="true"
              aria-labelledby="td-band-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="pg-modal-head">
                <div className="pg-modal-heading">
                  <span className="pg-modal-eyebrow">Students</span>
                  <h3 className="pg-modal-title" id="td-band-title">
                    {studentBand.title}
                  </h3>
                  <p className="pg-modal-sub">
                    {studentBand.rows.length} student
                    {studentBand.rows.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  aria-label="Close"
                  onClick={() => setStudentBand(null)}
                >
                  <FaTimes />
                </button>
              </header>

              <div className="pg-modal-body">
                {studentBand.rows.length === 0 ? (
                  <p className="lp-muted">No students in this band.</p>
                ) : (
                  <div className="lp-dialog-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          <th>Student</th>
                          <th>Batch</th>
                          <th>Attended</th>
                          <th>Rate</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {studentBand.rows.map((s, i) => (
                          <tr key={s.id || `${s.name}-${i}`}>
                            <td className="lp-num">{i + 1}</td>
                            <td className="lp-name">{s.name}</td>
                            <td>{s.batchName || "—"}</td>
                            <td>
                              {s.attended} of {s.offered}
                            </td>
                            <td
                              className={`td-band-rate td-band-rate--${
                                studentBand.tone === "good" ? "good" : "bad"
                              }`}
                            >
                              {s.rate}%
                            </td>
                            <td className="wp-rowact">
                              {s.id && (
                                <button
                                  type="button"
                                  className="wp-link"
                                  onClick={() => openEntity("student", s.id)}
                                >
                                  Open
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <footer className="pg-modal-foot">
                <div className="pgm-actions" />
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setStudentBand(null)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* Backdrop for the mobile filter sheet. Rendered only while a
            sheet is open, so it costs nothing on desktop. */}
        {filterSheet && (
          <div
            className="td-sheet-scrim"
            role="button"
            tabIndex={0}
            aria-label="Close filters"
            onClick={() => setFilterSheet(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape" || e.key === "Enter") setFilterSheet(null);
            }}
          />
        )}

        {/* Batch detail.

            Was a react-bootstrap <Modal className="premium-modal"> whose
            markup still carried Bootstrap utilities this project no
            longer ships -- bg-dark, text-dark, text-white, border-0,
            p-4, fw-bold. They only looked right because popups.css
            keeps a shim remapping bg-dark / bg-dark-2 / text-dark back
            to the theme INSIDE .premium-modal: a compatibility layer
            propping up dead classes. It also left this dialog as the one
            popup in the app that did not match the others.

            It is the house pg-modal now -- the same shell
            /mock-interviews and /attendance-calendar use -- so the shim,
            the Bootstrap modal dependency and the odd-one-out all go
            together. */}
        {showBatchModal && (
          <div
            className="pg-modal"
            role="presentation"
            onClick={() => setShowBatchModal(false)}
          >
            <div
              /* --wide, not --xl: .pg-modal-panel--xl is declared twice in
                 TrainerDashboard.css and the later one -- written for the
                 attendance roster -- forces `height: min(88vh,840px);
                 max-height: none`. A fixed height on a dialog that should
                 size to its content is what clipped this one's left panel
                 with no way to reach the rest. --wide keeps the base
                 max-height. */
              className="pg-modal-panel pg-modal-panel--wide"
              role="dialog"
              aria-modal="true"
              aria-labelledby="td-batch-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="pg-modal-head">
                <div className="pg-modal-heading">
                  <span className="pg-modal-eyebrow">Batch</span>
                  <h3 className="pg-modal-title" id="td-batch-title">
                    {selectedBatch?.name}
                  </h3>
                  <p className="pg-modal-sub">
                    {selectedBatch?.courseName || "Batch overview"}
                  </p>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  aria-label="Close"
                  onClick={() => setShowBatchModal(false)}
                >
                  <FaTimes />
                </button>
              </header>

              {/* Two tabs, like every other detail popup in the app.
                  This one was a Grid: a 4-wide column of facts beside an
                  8-wide roster, which meant the facts were a narrow strip
                  of wrapped labels and the roster never got the width a
                  list of people wants. They are different questions, and
                  lp-tabs is how this project asks two of them. */}
              <div className="lp-tabs" role="tablist" aria-label="Batch">
                {[
                  { key: "details", label: "Details" },
                  {
                    key: "students",
                    label: "Students",
                    n: selectedBatch?.studentCount ?? batchStudents.length,
                  },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    role="tab"
                    id={`td-batch-tab-${t.key}`}
                    aria-selected={batchTab === t.key}
                    aria-controls={`td-batch-panel-${t.key}`}
                    className={`lp-tab${batchTab === t.key ? " is-on" : ""}`}
                    onClick={() => setBatchTab(t.key)}
                  >
                    {t.label}
                    {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
                  </button>
                ))}
              </div>

              <div className="pg-modal-body">
            {selectedBatch && (
              <>
                <div
                  role="tabpanel"
                  id="td-batch-panel-details"
                  aria-labelledby="td-batch-tab-details"
                  hidden={batchTab !== "details"}
                >
                  <Card className="batch-info-card">
                    <CardHeader className="batch-info-header">
                      <h6>
                        <FaBookmark />
                        Batch Information
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <List variant="flush" className="batch-info-list">
                        <ListItem className="batch-info-item">
                          <strong>Course:</strong>{" "}
                          {/* Waypoint, when the record carries an id to open.
                              A course named in a batch is a course you can
                              read -- this popup does not have to know what a
                              course page looks like to get you there. */}
                          {selectedBatch.courseName && courseIdOf(selectedBatch) ? (
                            <button
                              type="button"
                              className="wp-link"
                              onClick={() => openEntity("course", courseIdOf(selectedBatch))}
                            >
                              {selectedBatch.courseName}
                            </button>
                          ) : (
                            selectedBatch.courseName || "N/A"
                          )}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Code:</strong>{" "}
                          {selectedBatch.courseCode || selectedBatch.courseId?.code || "N/A"}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Duration:</strong>{" "}
                          {selectedBatch.duration || selectedBatch.courseId?.duration || "N/A"}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Mode:</strong>{" "}
                          {getModeBadge(selectedBatch.mode)}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Branch:</strong>{" "}
                          {selectedBatch.branch || selectedBatch.courseId?.branchOfCourse || "N/A"}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Department:</strong>{" "}
                          {selectedBatch.department || selectedBatch.courseId?.departmentOfCourse ||
                            "N/A"}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Start Date:</strong>{" "}
                          {formatDate(selectedBatch.startTime)}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>End Date:</strong>{" "}
                          {formatDate(selectedBatch.endTime)}
                        </ListItem>
                        {/* The detail the card no longer carries. It is all
                            here, one click from the grid, instead of making
                            every card three screens tall. */}
                        <ListItem className="batch-info-item">
                          <strong>Class Time:</strong>{" "}
                          {formatTimeOfDay(selectedBatch.startTime)} – {formatTimeOfDay(selectedBatch.endTime)}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Days:</strong>{" "}
                          {formatDays(selectedBatch.days) || "N/A"}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Classroom:</strong>{" "}
                          {selectedBatch.classroomName || "Not assigned"}
                          {selectedBatch.classroomDetails?.floor && (
                            <span className="text-muted"> · Floor {selectedBatch.classroomDetails.floor}</span>
                          )}
                          {selectedBatch.classroomDetails?.capacity && (
                            <span className="text-muted"> · Seats {selectedBatch.classroomDetails.capacity}</span>
                          )}
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Students:</strong>{" "}
                          {selectedBatch.studentCount ?? batchStudents.length} enrolled
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Progress:</strong>{" "}
                          {selectedBatch.timelineProgress ?? 0}% of the timeline
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Resources:</strong>
                          <div className="batch-resource-row batch-resource-row--flush">
                            {selectedBatch.studyMaterialLink && (
                              <a href={selectedBatch.studyMaterialLink} target="_blank" rel="noopener noreferrer" className="batch-resource-chip">
                                <FaBookOpen size={11} /> Materials
                              </a>
                            )}
                            {selectedBatch.recordedLectureLink && (
                              <a href={selectedBatch.recordedLectureLink} target="_blank" rel="noopener noreferrer" className="batch-resource-chip">
                                <FaEye size={11} /> Recordings
                              </a>
                            )}
                            {selectedBatch.chatGroupId && (
                              <span className="batch-resource-chip batch-resource-chip--static">
                                <FaComments size={11} /> Chat group
                              </span>
                            )}
                            {!selectedBatch.studyMaterialLink &&
                              !selectedBatch.recordedLectureLink &&
                              !selectedBatch.chatGroupId && (
                                <span className="batch-resource-chip batch-resource-chip--empty">None linked</span>
                              )}
                          </div>
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Created:</strong> {formatDate(selectedBatch.createdAt)}
                          <span className="text-muted"> · Updated {formatDate(selectedBatch.updatedAt)}</span>
                        </ListItem>
                        <ListItem className="batch-info-item">
                          <strong>Batch ID:</strong>{" "}
                          <span className="batch-detail-value--mono">{selectedBatch.id || selectedBatch._id}</span>
                        </ListItem>
                        {/* The link itself is an action, so it lives in the
                            foot with Close. What stays here is the fact the
                            row carries: the id you read out to a class. */}
                        {selectedBatch.meetingLink && (
                          <ListItem className="batch-info-item">
                            <strong>Meeting ID:</strong>{" "}
                            <span className="batch-detail-value--mono">
                              {selectedBatch.meetingId || "N/A"}
                            </span>
                          </ListItem>
                        )}
                      </List>
                    </CardBody>
                  </Card>
                </div>

                <div
                  role="tabpanel"
                  id="td-batch-panel-students"
                  aria-labelledby="td-batch-tab-students"
                  /* hidden, not unmounted: the roster is fetched when the
                     batch opens, and remounting it on every tab flip
                     would throw away the scroll position of a list that
                     can run to a thousand rows. */
                  hidden={batchTab !== "students"}
                >
                  {/* The roster is a LIST, not a stack of cards. It was a
                      react-bootstrap Card holding a Grid of three Cells per
                      student, with a generic <FaUser/> glyph in a solid
                      accent circle, "Contact Person(s):" wrapping onto its
                      own line, and an "Open record" link floating at the
                      end -- an action column a view list does not have
                      (view_popup_rules), and an avatar that made every row
                      look identical when initials are what tell them apart.

                      The row is the control now, and it answers the question
                      this popup exists for: that student's attendance and
                      quiz marks ON THIS BATCH, under this trainer. The
                      person cell is the same .td-person the Attendance tab
                      already uses further up this file. */}
                  <section className="pgm-section">
                    <h3 className="pgm-section-title">
                      Students in this batch
                      <span className="pgm-section-count">{batchStudents.length}</span>
                    </h3>

                    {batchStudents.length === 0 ? (
                      <p className="pgm-empty">No students enrolled in this batch yet.</p>
                    ) : (
                      <table className="pgm-table pgm-students">
                        <thead>
                          <tr>
                            <th className="pgm-num">#</th>
                            <th>Student</th>
                            {/* Third: the column the popup drops below
                                575.98px, so it carries the least. */}
                            <th>Mobile</th>
                            <th>Contact person</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchStudents.map((student, index) => {
                            const name = student.studentName || student.name || "—";
                            const email = student.studentEmail || student.email || "";
                            const phone = student.studentNumber || student.phone || "";
                            const contacts =
                              Array.isArray(student.contactPersonIds) &&
                              student.contactPersonIds.length > 0
                                ? student.contactPersonIds
                                    .map((cp) => (typeof cp === "object" ? cp.name : cp))
                                    .filter(Boolean)
                                    .join(", ")
                                : "—";
                            const open = () =>
                              handleStudentClick(student, selectedBatch.id, selectedBatch.name);
                            return (
                              <tr
                                key={student.id || student.studentId || index}
                                className="pgm-row--open"
                                tabIndex={0}
                                role="button"
                                aria-label={`Open attendance and quizzes for ${name}`}
                                onClick={open}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    open();
                                  }
                                }}
                              >
                                <td className="pgm-num">{index + 1}</td>
                                <td>
                                  <div className="td-person">
                                    <span
                                      className={`td-person-avatar td-person-avatar--${avatarTone(
                                        name || email,
                                      )}`}
                                      aria-hidden="true"
                                    >
                                      {initialsOf(name, email)}
                                    </span>
                                    <span className="td-person-id">
                                      <b className="td-person-name">{name}</b>
                                      {email && (
                                        <small className="td-person-mail">{email}</small>
                                      )}
                                    </span>
                                  </div>
                                </td>
                                <td className="pgm-tel">{phone || "—"}</td>
                                <td>{contacts}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </section>
                </div>
              </>
            )}
              </div>
              {/* Head, tabs, body, foot -- the same four parts
                  /list-attendance's view popup has. Close is the only filled
                  button there, so the batch's own action sits beside it as an
                  outline one rather than competing with it. */}
              <footer className="pg-modal-foot">
                <div className="pgm-actions">
                  {selectedBatch?.meetingLink && (
                    <a
                      href={selectedBatch.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pgm-btn"
                    >
                      <FaVideo />
                      Join meeting
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setShowBatchModal(false)}
                >
                  Close
                </button>
              </footer>

              <StudentDetailModal
                show={showStudentModal}
                onHide={() => setShowStudentModal(false)}
                studentData={selectedStudentData}
                contactPersons={selectedStudentData?.student?.contactPersonIds || []}
              />
            </div>
          </div>
        )}

        {/* Profile Modal */}
        <Modal
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          size="lg"
          centered
          className="premium-modal"
        >
          <Modal.Header className="premium-modal-header border-0">
            <div className="modal-title-section">
              <FaEdit className="modal-icon" />
              <div>
                <Modal.Title className="text-white fw-bold mb-1">
                  Trainer Profile
                </Modal.Title>
                <p className="text-white-50 mb-0">
                  Manage your professional information
                </p>
              </div>
            </div>
            <Button
              variant="link"
              className="text-white p-0 border-0"
              onClick={() => setShowProfileModal(false)}
            >
              <FaTimes size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Grid>
                <Cell md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Trainer ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={trainer?.trainerId || ""}
                      disabled
                      className="form-control-premium"
                    />
                  </Form.Group>
                </Cell>
                <Cell md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Specialization
                    </Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={trainer?.specialization || ""}
                      className="form-control-premium"
                    />
                  </Form.Group>
                </Cell>
              </Grid>
              <Grid>
                <Cell md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">Experience</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={trainer?.experience || ""}
                      className="form-control-premium"
                    />
                  </Form.Group>
                </Cell>
              </Grid>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 p-4">
            <Button
              variant="secondary"
              onClick={() => setShowProfileModal(false)}
              className="premium-btn-secondary"
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* The attendance tab's Export CSV. Portalled to <body>, so it
            is written here rather than inside the tab's own markup. */}
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          title="Student attendance"
          columns={attendanceExportColumns}
          rows={attendanceRows}
          filenameBase="attendance"
          scopeNote={
            attendanceRows.length < attendancePagination.total
              ? `${attendanceRows.length} of ${attendancePagination.total} rows are loaded — scroll the table to bring in the rest first.`
              : attendanceFilterCount > 0
                ? `Everything matching the ${attendanceFilterCount} filter${
                    attendanceFilterCount === 1 ? "" : "s"
                  } on the table.`
                : undefined
          }
        />
      </Page>
    </div>
  );
};

export default TrainerDashboard;
